import { useAdminContext } from '@/contexts/AdminContext';
import { type Translate, useTranslation } from '@client-app/i18n';
import { Permission, useAuth } from '@/contexts/AuthContext';
import type { UseWorkspaceIntegrationsReturn } from '@/contexts/AuthContext/useWorkspaceIntegrations';
import { useDialog } from '@/contexts/DialogContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useToast } from '@/contexts/ToastContext';
import {
    authService,
    type StoreTrialEligibilityState,
} from '@common/api/authService';
import {
    featureService,
    type IntegrationPermission,
} from '@common/api/featureService';
import {
    mcpConnectorService,
    type McpConnectorResponse,
    type McpConnectorTool,
    type McpConnectorToolUpdateRequest,
    type McpCredentialRequest,
    type NormalizedMcpConnectorInput,
} from '@common/api/mcpConnectorService';
import Skeleton from '@common/components/Skeleton';
import {
    CalendarBlank,
    CaretDown,
    CaretLeft,
    CaretRight,
    ChartBar,
    ChatCircleText,
    CircleNotch,
    Database,
    FileText,
    Kanban,
    Lightning,
    ListChecks,
    MagnifyingGlass,
    Package,
    PencilSimple,
    Percent,
    Plus,
    ShoppingBag,
    ShoppingCart,
    SlidersHorizontal,
    SquaresFour,
    Storefront,
    Table,
    Trash,
    Users,
    XCircle,
    type IconProps
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
    useCallback, useEffect, useMemo, useRef, useState, type ComponentType
} from 'react';
import ContactSalesModal from '../../Pricing/ContactSalesModal';
import type { ConnectionData } from './integrations/ConnectionCard';
import {
    getConnectRestrictionAction,
    resolveConnectRestrictionCopyContext,
    type StoreLimitPrompt,
} from './integrations/connectRestriction';
import GoogleAdsAccountSelectionModal from './integrations/GoogleAdsAccountSelectionModal';
import {
    getBrandIcon,
    getBrandIconFrameClassName,
    getBrandIconImageClassName,
} from './integrations/IntegrationCard';
import {
    AVAILABLE_INTEGRATION_STATUS,
    getIntegrationConnectionStatusMeta,
    isIntegrationConnectionActive,
    resolveKlaviyoIntegrationAvailability,
} from './integrations/integrationStatus';
import {
    getIntegrationCatalogGroups,
    getIntegrationCatalogTools,
    getIntegrationProviderForTool,
    INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO,
    isConnectableIntegrationTool,
    MCP_TOOL_NAME,
    normalizeIntegrationProvider,
} from './integrations/integrationCatalog';
import {
    resolveDefaultIntegrationDetailTab,
    type IntegrationDetailTab,
} from './integrations/integrationDetailTabs';
import IntegrationApiKeyModal from './integrations/IntegrationApiKeyModal';
import IntegrationConnectGuide, { getIntegrationConnectGuideModalWidth } from './integrations/IntegrationConnectGuide';
import IntegrationUrlModal from './integrations/IntegrationUrlModal';
import McpConnectorModal, {
    type McpConnectorModalMode,
} from './integrations/McpConnectorModal';
import {
    createWorkspaceRequestGuard,
    useMcpConnectorFlow,
} from './integrations/useMcpConnectorFlow';
import { getStaticIntegrationCapabilityCopy } from './integrations/staticIntegrationCapabilityCatalog';
import { normalizeShopifyStoreDomainInput } from '../../Onboarding/storeUrlNormalization';
import ConnectionAccessSettings from './ConnectionAccessSettings';
import {
    isSoloInviteUpgradeRequired,
    resolveInvitePlanCode,
} from './memberInviteAccess';
import Modal from './Modal';

type CapabilityMode = 'read' | 'write';

interface CapabilityItem {
    id: string;
    label: string;
}

interface CapabilitySection {
    id: string;
    title: string;
    icon: ComponentType<IconProps>;
    items: CapabilityItem[];
}

interface CapabilityCatalog {
    read: CapabilitySection[];
    write: CapabilitySection[];
}

const buildCapabilityItem = (id: string, label: string): CapabilityItem => ({
    id,
    label,
});

const countCapabilityItems = (sections: CapabilitySection[]) => (
    sections.reduce((total, section) => total + section.items.length, 0)
);

const flattenCapabilityItemIds = (sections: CapabilitySection[]) => (
    sections.flatMap(section => section.items.map(item => item.id))
);

const getCapabilityAccessLabel = (t: Translate, readCount: number, writeCount: number) => {
    if (readCount > 0 && writeCount > 0) {
        return t('website.integrationsSettings.readAndWrite');
    }
    if (readCount > 0) {
        return t('website.integrationsSettings.readOnly');
    }
    if (writeCount > 0) {
        return t('website.integrationsSettings.writeOnly');
    }
    return t('website.integrationsSettings.noAccess');
};

const getActiveCapabilityItemIds = (
    tool: string,
    connection: ConnectionData | undefined,
    mode: CapabilityMode,
    catalog: CapabilityCatalog,
) => {
    const sections = catalog[mode];
    const summary = connection?.permissions?.[mode]?.trim();

    // Binary Toggle Logic: If we have any summary for this mode (Read/Write),
    // and it's not 'None', then ALL items in this catalog mode are considered active.
    if (!summary || summary === 'None' || sections.length === 0) {
        return new Set<string>();
    }

    return new Set(flattenCapabilityItemIds(sections));
};

const TOOL_TO_PERMISSION_PLATFORM: Record<string, string> = {
    shopify: 'SHOPIFY',
    'judge.me': 'JUDGEME',
    google_ads: 'GOOGLE_ADS',
    meta_ads: 'META_ADS',
    facebook_page: 'FACEBOOK_PAGE',
    instagram: 'INSTAGRAM',
    linkedin_ads: 'LINKEDIN_ADS',
    pinterest_ads: 'PINTEREST_ADS',
    x_ads: 'X_ADS',
    tiktok_ads: 'TIKTOK_ADS',
    youtube_ads: 'YOUTUBE',
    snapchat_ads: 'SNAPCHAT',
    google_analytics: 'GOOGLE_ANALYTICS',
    google_search_console: 'GOOGLE_SEARCH_CONSOLE',
    bigcommerce: 'BIGCOMMERCE',
    woocommerce: 'WOOCOMMERCE',
    magento: 'MAGENTO',
    prestashop: 'PRESTASHOP',
    aliexpress: 'ALIEXPRESS',
    wix: 'WIX',
    klaviyo: 'KLAVIYO',
    semrush: 'SEMRUSH',
    similarweb: 'SIMILARWEB',
    ahrefs: 'AHREFS',
    omnisend: 'OMNISEND',
    okendo: 'OKENDO',
    insider_one: 'INSIDER_ONE',
    emarsys: 'EMARSYS',
    mailchimp: 'MAILCHIMP',
    feishu: 'FEISHU',
    figma: 'FIGMA',
    'figma-oauth': 'FIGMA',
    figma_oauth: 'FIGMA',
    'figma-api-key': 'FIGMA',
    figma_api_key: 'FIGMA',
};

const PERMISSION_GROUP_ICONS: Record<string, ComponentType<IconProps>> = {
    account: Database,
    store: Storefront,
    products: ShoppingBag,
    orders: ShoppingCart,
    customers: Users,
    inventory: Package,
    discounts: Percent,
    analytics: ChartBar,
    campaigns: Lightning,
    accounts: Database,
    messages: ChatCircleText,
    chats: Users,
    comments: ChatCircleText,
    contacts: Users,
    calendar: CalendarBlank,
    docs: FileText,
    files: FileText,
    sheets: Table,
    bitable: Kanban,
    tasks: ListChecks,
    libraries: Kanban,
    variables: Database,
    dev_resources: FileText,
    webhooks: Lightning,
};

const buildCatalogFromIntegrationPermissions = (
    tool: string,
    integrationPermissions: IntegrationPermission[] = [],
    _t?: Translate,
): CapabilityCatalog | null => {
    const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
    const platform = TOOL_TO_PERMISSION_PLATFORM[normalizedTool];
    if (!platform) {
        return null;
    }

    const catalog: CapabilityCatalog = { read: [], write: [] };
    const sectionMaps: Record<CapabilityMode, Map<string, CapabilitySection>> = {
        read: new Map(),
        write: new Map(),
    };

    integrationPermissions
        .filter(permission => permission.platform === platform && String(permission.status || '').toUpperCase() === 'ACTIVE')
        .sort((left, right) => (
            (left.groupSortOrder ?? 100) - (right.groupSortOrder ?? 100)
            || (left.sortOrder ?? 100) - (right.sortOrder ?? 100)
        ))
        .forEach((permission) => {
            const action = String(permission.action || '').toUpperCase() === 'WRITE' ? 'write' : 'read';
            const sectionKey = permission.groupKey || 'general';
            const sectionMap = sectionMaps[action];
            let section = sectionMap.get(sectionKey);

            if (!section) {
                section = {
                    id: sectionKey,
                    title: permission.groupTitle || permission.groupKey || sectionKey,
                    icon: PERMISSION_GROUP_ICONS[sectionKey] || Database,
                    items: [],
                };
                sectionMap.set(sectionKey, section);
                catalog[action].push(section);
            }

            section.items.push(buildCapabilityItem(
                permission.permissionKey,
                permission.label || permission.permissionKey,
            ));
        });

    return catalog.read.length > 0 || catalog.write.length > 0 ? catalog : null;
};

const getCapabilityCatalog = (
    tool: string,
    _connection?: ConnectionData,
    integrationPermissions: IntegrationPermission[] = [],
    t?: Translate,
): CapabilityCatalog => {
    const backendCatalog = buildCatalogFromIntegrationPermissions(tool, integrationPermissions, t);
    if (backendCatalog) {
        return backendCatalog;
    }

    return { read: [], write: [] };
};

const buildStaticCapabilitySections = (
    tool: string,
    mode: CapabilityMode,
    labels: string[],
    t: Translate,
): CapabilitySection[] => {
    if (labels.length === 0) {
        return [];
    }

    const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
    return [{
        id: `${normalizedTool}:${mode}`,
        title: mode === 'read'
            ? t('website.integrationsSettings.whatBustlyCanView')
            : t('website.integrationsSettings.whatBustlyCanDo'),
        icon: Database,
        items: labels.map((label, index) => buildCapabilityItem(
            `static:${normalizedTool}:${mode}:${index}`,
            label,
        )),
    }];
};

const getDisplayCapabilityCatalog = (
    tool: string,
    t: Translate,
): CapabilityCatalog => {
    const staticCopy = getStaticIntegrationCapabilityCopy(tool, t);
    if (!staticCopy) {
        return { read: [], write: [] };
    }

    return {
        read: buildStaticCapabilitySections(tool, 'read', staticCopy.read, t),
        write: buildStaticCapabilitySections(tool, 'write', staticCopy.write, t),
    };
};

const getConnectionPrimaryLabel = (t: Translate, tool: string) => {
    if (tool === 'Shopify') {
        return t('website.integrationsSettings.storeUrl');
    }

    return t('website.integrationsSettings.account');
};

const getConnectionIdentityKey = ({
    workspaceIntegrationId,
    connectionId,
    id,
}: Pick<ConnectionData, 'workspaceIntegrationId' | 'connectionId' | 'id'>) => (
    String(workspaceIntegrationId || connectionId || id)
);

const mapMcpConnectorStatusToConnectionStatus = (status?: string | null): ConnectionData['status'] => {
    const normalized = String(status || '').toUpperCase();
    if (normalized === 'ACTIVE') {
        return 'connected';
    }
    if (normalized === 'FAILED' || normalized === 'ERROR') {
        return 'error';
    }
    return 'disconnected';
};

const findNearestScrollContainer = (element: HTMLElement | null) => {
    if (typeof window === 'undefined') {
        return null;
    }

    let current = element?.parentElement || null;

    while (current) {
        const { overflowY } = window.getComputedStyle(current);

        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            return current;
        }

        current = current.parentElement;
    }

    return document.scrollingElement instanceof HTMLElement
        ? document.scrollingElement
        : null;
};

type IntegrationsSettingsTimingKey = 'workspaceDataAccess' | 'integrationPermissions';

type IntegrationsSettingsTimingTracker = {
    record: (key: IntegrationsSettingsTimingKey, elapsedMs: number) => void;
    toLogPayload: () => Record<string, unknown>;
};

const nowForIntegrationsSettingsTiming = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
);

const createIntegrationsSettingsTimingTracker = (workspaceId?: string): IntegrationsSettingsTimingTracker => {
    const startedAt = nowForIntegrationsSettingsTiming();
    const timings: Partial<Record<IntegrationsSettingsTimingKey, number>> = {};

    return {
        record: (key, elapsedMs) => {
            timings[key] = Math.round(elapsedMs);
        },
        toLogPayload: () => ({
            workspaceId,
            totalMs: Math.round(nowForIntegrationsSettingsTiming() - startedAt),
            ...timings,
        }),
    };
};

const trackIntegrationsSettingsTiming = async <T,>(
    timingTracker: IntegrationsSettingsTimingTracker,
    key: IntegrationsSettingsTimingKey,
    promise: PromiseLike<T>
): Promise<T> => {
    const startedAt = nowForIntegrationsSettingsTiming();
    try {
        return await promise;
    } finally {
        timingTracker.record(key, nowForIntegrationsSettingsTiming() - startedAt);
    }
};

/**
 * IntegrationsSettings 组件
 *
 * 集成状态数据源变更：
 * - 旧版本：从 profile.shopInfos (Shopify) 和 nangoIntegrations (其他平台) 混合获取
 * - 新版本：统一从 workspace_integrations 表通过 Supabase 拉取
 *
 * workspaceIntegrations 提供的接口：
 * - workspaceIntegrations.shopify - Shopify 集成数据
 * - workspaceIntegrations.bigCommerce - BigCommerce 集成数据
 * - workspaceIntegrations.googleAds - Google Ads 集成数据
 * - workspaceIntegrations.googleAnalytics - Google Analytics 集成数据
 * - workspaceIntegrations.tiktokAds - TikTok Ads 集成数据
 * - workspaceIntegrations.googleSearchConsole - Google Search Console 集成数据
 * - workspaceIntegrations.semrush - Semrush 集成数据
 * - workspaceIntegrations.slack - Slack 集成数据
 * - workspaceIntegrations.woocommerce - WooCommerce 集成数据
 * - workspaceIntegrations.magento - Magento 集成数据
 * - workspaceIntegrations.prestashop - PrestaShop 集成数据
 * - workspaceIntegrations.wix - Wix 集成数据
 *
 * 每个平台的集成数据结构：
 * - status: 'ACTIVE' | 'INACTIVE' | 'ERROR' - 连接状态
 * - connected_at: string - 连接时间
 * - nango_connection_id: string - Nango 连接 ID
 * - platform_shop_name: string | null - 平台店铺名称
 */

const MCP_UI_ENABLED = false;
const isMcpUiEnabled = () => MCP_UI_ENABLED;

const isMcpTool = (tool: string) => tool === MCP_TOOL_NAME;

const mcpToolAccessOptions: Array<NonNullable<McpConnectorToolUpdateRequest['accessLevelRequired']>> = ['READ', 'WRITE'];
const mcpToolRiskOptions: Array<NonNullable<McpConnectorToolUpdateRequest['riskLevel']>> = ['LOW', 'MEDIUM', 'HIGH'];

const ZERNIO_DIRECT_CONNECT_TOOLS = new Set([
    'Meta Ads',
    'Facebook Page',
    'Instagram',
    'Pinterest Ads',
    'X Ads',
]);

const ZERNIO_CONNECT_TIMEOUT_MS = 5 * 60 * 1000;

const withZernioConnectTimeout = async <T,>(tool: string, promise: Promise<T>): Promise<T> => {
    if (!ZERNIO_DIRECT_CONNECT_TOOLS.has(tool)) {
        return promise;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(`${tool} authorization did not finish. Please refresh and try again.`));
                }, ZERNIO_CONNECT_TIMEOUT_MS);
            }),
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

const CONNECT_PROVIDER_TO_TOOL: Record<string, string> = {
    'mcp': MCP_TOOL_NAME,
    'custom-mcp': MCP_TOOL_NAME,
    'custom-mcp-connector': MCP_TOOL_NAME,
    'shopify': 'Shopify',
    'bigcommerce': 'BigCommerce',
    'woocommerce': 'WooCommerce',
    'magento': 'Magento',
    'adobe-commerce': 'Magento',
    'prestashop': 'PrestaShop',
    'presta-shop': 'PrestaShop',
    'wix': 'Wix',
    'google-ads': 'Google Ads',
    'googleads': 'Google Ads',
    'google-analytics': 'Google Analytics',
    'tiktok-ads': 'TikTok Ads',
    'google-search-console': 'Google Search Console',
    'gsc': 'Google Search Console',
    'semrush': 'Semrush',
    'klaviyo': 'Klaviyo',
    'klaviyo-oauth': 'Klaviyo',
    'mailchimp': 'Mailchimp',
    'mailchimp-oauth': 'Mailchimp',
    'feishu': 'Feishu',
    'figma': 'Figma',
    'figma-oauth': 'Figma',
    'similarweb': 'Similarweb',
    'similar-web': 'Similarweb',
    'ahrefs': 'Ahrefs',
    'omnisend': 'Omnisend',
    'okendo': 'Okendo',
    'insider-one': 'Insider One',
    'insider-one-oauth': 'Insider One',
    'insider': 'Insider One',
    'useinsider': 'Insider One',
    'emarsys': 'Emarsys',
    'emarsys-oauth': 'Emarsys',
    'sap-emarsys': 'Emarsys',
    'sap-engagement-cloud': 'Emarsys',
    'judgeme': 'Judge.me',
    'judgeme-oauth': 'Judge.me',
    'judge-me': 'Judge.me',
    'judge.me': 'Judge.me',
    'judge': 'Judge.me',
    'aliexpress': 'Aliexpress',
    'slack': 'Slack',
    'meta-ads': 'Meta Ads',
    'metaads': 'Meta Ads',
    'facebook-page': 'Facebook Page',
    'facebook': 'Facebook Page',
    'instagram': 'Instagram',
    'linkedin-ads': 'LinkedIn Ads',
    'linkedinads': 'LinkedIn Ads',
    'linkedin': 'LinkedIn Ads',
    'pinterest-ads': 'Pinterest Ads',
    'pinterestads': 'Pinterest Ads',
    'pinterest': 'Pinterest Ads',
    'x-ads': 'X Ads',
    'xads': 'X Ads',
    'twitter-ads': 'X Ads',
    'twitterads': 'X Ads',
    'twitter-x': 'X Ads',
    'twitterx': 'X Ads',
    'twitter': 'X Ads',
    'x': 'X Ads',
    'youtube-ads': 'YouTube Ads',
    'youtubeads': 'YouTube Ads',
    'youtube': 'YouTube Ads',
    'snapchat-ads': 'Snapchat Ads',
    'snapchatads': 'Snapchat Ads',
    'snapchat': 'Snapchat Ads',
};

const normalizeConnectProvider = (value?: string | null) => (
    value || ''
).trim().toLowerCase().replace(/[\s_]+/g, '-');

const getToolFromConnectProvider = (value?: string | null) => {
    const normalized = normalizeConnectProvider(value);
    return CONNECT_PROVIDER_TO_TOOL[normalized] || null;
};

type FeishuCustomAppRegion = 'feishu';

const FEISHU_REQUIRED_BASE_SCOPE = 'offline_access';

const getFeishuOAuthRedirectUri = () => {
    const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
    return supabaseUrl ? `${supabaseUrl}/functions/v1/feishu-oauth-callback` : '';
};

const ALL_INTEGRATIONS = getIntegrationCatalogGroups(isMcpUiEnabled());
const ALL_INTEGRATION_TOOLS = getIntegrationCatalogTools(isMcpUiEnabled());

const ALL_INTEGRATION_TOOL_ORDER = new Map(
    ALL_INTEGRATION_TOOLS.map((tool, index) => [tool, index])
);

const SCENARIO_FILTERS = [
    { id: 'Connected', label: 'Connected', icon: 'connect' },
    { id: 'Store Ops', label: 'Store Ops', icon: 'store' },
    { id: 'Advertising', label: 'Advertising', icon: 'dashboard' },
    { id: 'Email Marketing', label: 'Email Marketing', icon: 'send' },
    { id: 'Analytics', label: 'Analytics', icon: 'pulse' },
    { id: 'SEO', label: 'SEO', icon: 'search' },
    { id: 'Social Media', label: 'Social Media', icon: 'users' },
    { id: 'Customers', label: 'Customers', icon: 'profile' },
    { id: 'Finance', label: 'Finance', icon: 'billing' },
    { id: 'Team Collaboration', label: 'Team Collaboration', icon: 'users' },
    ...(isMcpUiEnabled() ? [{ id: 'MCP', label: 'MCP', icon: 'database' }] : []),
] as const;
type ScenarioFilter = typeof SCENARIO_FILTERS[number]['id'];

const DEFAULT_SCENARIO_FILTER: ScenarioFilter = 'Connected';

const INTEGRATION_SEARCH_KEYWORDS: Record<string, string[]> = {
    [MCP_TOOL_NAME]: ['mcp', 'model context protocol', 'custom connector', 'custom tool'],
    'Google Search Console': ['gsc', 'search console', 'google webmaster', 'google webmasters'],
    'Similarweb': ['similar web', 'competitive intelligence', 'website traffic'],
    'Ahrefs': ['backlinks', 'seo', 'domain rating'],
    'Omnisend': ['email marketing', 'sms marketing', 'lifecycle marketing', 'newsletter'],
    'Okendo': ['reviews', 'ratings', 'ugc', 'survey responses', 'customer feedback'],
    'Insider One': ['insider', 'useinsider', 'customer engagement', 'personalization'],
    'Emarsys': ['sap emarsys', 'sap engagement cloud', 'email marketing', 'customer engagement'],
    'X Ads': ['twitter ads', 'twitter x', 'twitter'],
    'YouTube Ads': ['youtube', 'youtube channel', 'youtube content'],
    'Snapchat Ads': ['snap', 'snapchat'],
    'Feishu': ['team collaboration', 'docs', 'sheets', 'calendar', 'bitable'],
    'Figma': ['design', 'design system', 'figma file', 'components', 'comments', 'dev resources'],
};

const REGISTRATION_URLS: Record<string, string> = {
    Shopify: 'https://www.shopify.com/free-trial',
    Aliexpress: 'https://seller.aliexpress.com/',
    'Google Ads': 'https://ads.google.com/',
    'Google Search Console': 'https://search.google.com/search-console',
    'LinkedIn Ads': 'https://www.linkedin.com/campaignmanager/',
    'Pinterest Ads': 'https://ads.pinterest.com/',
    'X Ads': 'https://ads.x.com/',
    'YouTube Ads': 'https://ads.google.com/',
    'Snapchat Ads': 'https://forbusiness.snapchat.com/advertising',
    Semrush: 'https://www.semrush.com/signup/',
    Similarweb: 'https://www.similarweb.com/corp/pricing/',
    Ahrefs: 'https://ahrefs.com/pricing',
    Omnisend: 'https://app.omnisend.com/registration/',
    Okendo: 'https://reviews.okendo.io/admin/',
    PrestaShop: 'https://prestashop.com/',
    'Insider One': 'https://useinsider.com/',
    Emarsys: 'https://emarsys.com/',
    Wix: 'https://www.wix.com/',
    Feishu: 'https://www.feishu.cn/',
    Figma: 'https://www.figma.com/',
};

const API_KEY_TOOLS = new Set(['Ahrefs', 'Omnisend', 'Figma', 'Okendo']);
const MANUAL_CREDENTIAL_TOOLS = new Set([...API_KEY_TOOLS, 'PrestaShop']);
const API_KEY_FALLBACK_TOOLS = new Set(['Insider One']);
const API_KEY_CONNECT_PROVIDER: Record<string, string> = {
    'Insider One': 'insider-one-api-key',
};

const normalizeSearchText = (value: string) => value.toLowerCase().trim().replace(/\s+/g, ' ');
const normalizeConnectionMatchValue = (value?: string | null) =>
    String(value || '').trim().toLowerCase();
const formatIntegerForDisplay = (value: number, locale: string = 'en') => (
    new Intl.NumberFormat(locale === 'zh-CN' ? 'zh-CN' : 'en-US', {
        maximumFractionDigits: 0,
    }).format(value)
);
const getToolDescription = (t: Translate, tool: string) => {
    switch (tool) {
        case MCP_TOOL_NAME: return t('website.integrationsSettings.toolDescriptions.customMcp');
        case 'Shopify': return t('website.integrationsSettings.toolDescriptions.shopify');
        case 'BigCommerce': return t('website.integrationsSettings.toolDescriptions.bigCommerce');
        case 'WooCommerce': return t('website.integrationsSettings.toolDescriptions.wooCommerce');
        case 'Magento': return t('website.integrationsSettings.toolDescriptions.magento');
        case 'PrestaShop': return t('website.integrationsSettings.toolDescriptions.prestashop');
        case 'Wix': return t('website.integrationsSettings.toolDescriptions.wix');
        case 'Google Ads': return t('website.integrationsSettings.toolDescriptions.googleAds');
        case 'TikTok Ads': return t('website.integrationsSettings.toolDescriptions.tikTokAds');
        case 'Meta Ads': return t('website.integrationsSettings.toolDescriptions.metaAds');
        case 'LinkedIn Ads': return t('website.integrationsSettings.toolDescriptions.linkedInAds');
        case 'Pinterest Ads': return t('website.integrationsSettings.toolDescriptions.pinterestAds');
        case 'X Ads': return t('website.integrationsSettings.toolDescriptions.xAds');
        case 'YouTube Ads': return t('website.integrationsSettings.toolDescriptions.youtubeAds');
        case 'Snapchat Ads': return t('website.integrationsSettings.toolDescriptions.snapchatAds');
        case 'Google Search Console': return t('website.integrationsSettings.toolDescriptions.googleSearchConsole');
        case 'Google Analytics': return t('website.integrationsSettings.toolDescriptions.googleAnalytics');
        case 'Klaviyo': return t('website.integrationsSettings.toolDescriptions.klaviyo');
        case 'Mailchimp': return t('website.integrationsSettings.toolDescriptions.mailchimp');
        case 'Omnisend': return t('website.integrationsSettings.toolDescriptions.omnisend');
        case 'Okendo': return t('website.integrationsSettings.toolDescriptions.okendo');
        case 'Insider One': return t('website.integrationsSettings.toolDescriptions.insiderOne');
        case 'Emarsys': return t('website.integrationsSettings.toolDescriptions.emarsys');
        case 'Judge.me': return t('website.integrationsSettings.toolDescriptions.judgeme');
        case 'Facebook Page': return t('website.integrationsSettings.toolDescriptions.facebookPage');
        case 'Instagram': return t('website.integrationsSettings.toolDescriptions.instagram');
        case 'Feishu': return t('website.integrationsSettings.toolDescriptions.feishu');
        case 'Figma': return t('website.integrationsSettings.toolDescriptions.figma');
        case 'Slack': return t('website.integrationsSettings.toolDescriptions.slack');
        case 'Semrush': return t('website.integrationsSettings.toolDescriptions.semrush');
        case 'Similarweb': return t('website.integrationsSettings.toolDescriptions.similarweb');
        case 'Ahrefs': return t('website.integrationsSettings.toolDescriptions.ahrefs');
        case 'Aliexpress': return t('website.integrationsSettings.toolDescriptions.aliexpress');
        case 'Squarespace': return t('website.integrationsSettings.toolDescriptions.squarespace');
        default: return t('website.integrationsSettings.toolDescriptions.default', { tool });
    }
};

const formatConnectionCountLabel = (t: Translate, connectionCount: number) => {
    if (connectionCount <= 0) {
        return null;
    }

    return t('website.integrationsSettings.connectionCount', {
        count: connectionCount,
        plural: connectionCount === 1 ? '' : 's',
    });
};

const formatConnectionTimestamp = (locale: string, connectedAt: string) => {
    if (!connectedAt || connectedAt === 'Recent') {
        return null;
    }

    const parsedDate = new Date(connectedAt);
    if (Number.isNaN(parsedDate.getTime())) {
        return connectedAt;
    }

    const includesExplicitTime = connectedAt.includes('T') || /\d:\d{2}/.test(connectedAt);
    return new Intl.DateTimeFormat(
        locale === 'zh-CN' ? 'zh-CN' : 'en-US',
        includesExplicitTime
            ? {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: false,
            }
            : {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            }
    ).format(parsedDate);
};

const formatConnectionMetaLine = (
    t: Translate,
    locale: string,
    connection: Pick<ConnectionData, 'connectedAt' | 'connectedBy'>
) => {
    const formattedTimestamp = formatConnectionTimestamp(locale, connection.connectedAt);

    if (!formattedTimestamp) {
        return connection.connectedBy
            ? t('website.integrationsSettings.connectedRecentlyBy', { user: connection.connectedBy })
            : t('website.integrationsSettings.connectedRecently');
    }

    return connection.connectedBy
        ? t('website.integrationsSettings.connectedByOn', {
            user: connection.connectedBy,
            date: formattedTimestamp,
        })
        : t('website.integrationsSettings.connectedOn', { date: formattedTimestamp });
};

const normalizeConnectionStatusValue = (
    status: unknown,
    fallback: ConnectionData['status'] = 'disconnected'
): ConnectionData['status'] => {
    const normalized = String(status ?? '').trim().toLowerCase();

    if (!normalized) {
        return fallback;
    }

    if (
        normalized === '1'
        || normalized === 'true'
        || normalized === 'active'
        || normalized === 'connected'
    ) {
        return 'connected';
    }

    if (
        normalized === '0'
        || normalized === 'false'
        || normalized === 'inactive'
        || normalized === 'disconnected'
    ) {
        return 'disconnected';
    }

    if (normalized === 'error' || normalized === 'failed') {
        return 'error';
    }

    if (
        normalized === 'disabled'
        || normalized === 'suspended'
        || normalized === 'trial_expired'
    ) {
        return normalized as ConnectionData['status'];
    }

    return fallback;
};

const matchesIntegrationSearch = (tool: string, query: string) => {
    if (!query.trim()) {
        return true;
    }

    const normalizedQuery = normalizeSearchText(query);
    const normalizedTool = normalizeSearchText(tool);

    if (normalizedTool.includes(normalizedQuery)) {
        return true;
    }

    const keywords = INTEGRATION_SEARCH_KEYWORDS[tool] || [];
    return keywords.some(keyword => normalizeSearchText(keyword).includes(normalizedQuery));
};

const DIRECT_CONNECT_DIALOG_CLOSED_MESSAGES = new Set([
    'WooCommerce connection dialog was closed',
    'Magento connection dialog was closed',
]);
const DISCONNECT_LOADING_DURATION_MS = 3000;

const isDirectConnectDialogClosed = (message: string) => {
    const normalizedMessage = message.toLowerCase();
    return Array.from(DIRECT_CONNECT_DIALOG_CLOSED_MESSAGES)
        .some(dialogClosedMessage => normalizedMessage.includes(dialogClosedMessage.toLowerCase()));
};

const normalizeConnectionUserMessage = (message?: string | null, fallback = 'Connection failed. Please try again.') => {
    if (!message) {
        return fallback;
    }

    const cleanedMessage = message
        .replace(/Zernio/gi, 'Bustly')
        .replace(/Zerinio/gi, 'Bustly')
        .replace(/zerinio/gi, 'Bustly')
        .replace(/zernio/gi, 'Bustly');

    const judgeMeMismatch = cleanedMessage.match(/Judge\.me authorized shop ([a-z0-9-]+(?:\.[a-z0-9-]+)+) does not match requested shop ([a-z0-9-]+(?:\.[a-z0-9-]+)+)/i);
    if (judgeMeMismatch) {
        const [, authorizedShop, requestedShop] = judgeMeMismatch;
        return `Bustly requested ${requestedShop}, but Judge.me returned authorization for ${authorizedShop}. This usually means the Judge.me popup is still signed in to ${authorizedShop}. Sign out or switch stores in Judge.me, then authorize ${requestedShop}.`;
    }

    if (/Judge\.me shop info API error .*Shop domain or Api Token is wrong/i.test(cleanedMessage)) {
        return 'Judge.me could not verify this store. Make sure Judge.me is installed on that Shopify store, then authorize with the same store account.';
    }

    return cleanedMessage;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getReadableDisplayValue = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue || UUID_PATTERN.test(trimmedValue)) {
        return null;
    }

    return trimmedValue;
};

const resolveConnectionUserDisplayName = (
    mapping?: Record<string, any>,
    profile: Record<string, any> = {}
) => {
    const profileUser = profile.user || profile.connected_by || profile.created_by || {};
    const displayName = [
        mapping?.connected_by_name,
        mapping?.user_name,
        mapping?.created_by_name,
        mapping?.connectedByName,
        mapping?.userName,
        mapping?.createdByName,
        profile.connected_by_name,
        profile.user_name,
        profile.created_by_name,
        profile.name,
        profile.full_name,
        profileUser.name,
        profileUser.full_name,
        mapping?.authorized_account_email,
        mapping?.authorizedAccountEmail,
        mapping?.external_account_email,
        mapping?.externalAccountEmail,
        mapping?.connected_by_email,
        mapping?.user_email,
        mapping?.created_by_email,
        mapping?.email,
        profile.authorized_account_email,
        profile.authorizedAccountEmail,
        profile.external_account_email,
        profile.externalAccountEmail,
        profile.connected_by_email,
        profile.user_email,
        profile.created_by_email,
        profile.email,
        profileUser.authorized_account_email,
        profileUser.authorizedAccountEmail,
        profileUser.external_account_email,
        profileUser.externalAccountEmail,
        profileUser.email,
        mapping?.connected_by,
        mapping?.created_by,
        mapping?.user_id,
    ].map(getReadableDisplayValue).find(Boolean);

    if (displayName) {
        return displayName;
    }

    return 'Admin';
};

const getNonEmptyDisplayValue = (value: unknown): string | null => {
    if (value == null) {
        return null;
    }

    const text = String(value).trim();
    return text || null;
};

const FEISHU_INTERNAL_ACCOUNT_PATTERN = /^feishu:[^:]+:.+$/i;
const WORKSPACE_INTEGRATION_SLUG_PATTERN = /^wi_[0-9a-z]{10}$/i;

const getFeishuReadableDisplayValue = (value: unknown): string | null => {
    const text = getNonEmptyDisplayValue(value);
    if (!text || FEISHU_INTERNAL_ACCOUNT_PATTERN.test(text) || WORKSPACE_INTEGRATION_SLUG_PATTERN.test(text)) {
        return null;
    }

    return text;
};

const resolveFeishuConnectionDisplayName = (
    profile: Record<string, any> = {},
    integration: Record<string, any> = {},
    mapping?: Record<string, any>,
) => {
    const integrationProfile = integration.profile_data || {};
    return [
        mapping?.external_account_name,
        mapping?.externalAccountName,
        integration.external_account_name,
        integration.externalAccountName,
        mapping?.authorized_account_name,
        mapping?.authorizedAccountName,
        profile.authorized_account_name,
        profile.authorizedAccountName,
        profile.external_account_name,
        profile.externalAccountName,
        profile.accountName,
        profile.name,
        integrationProfile.authorized_account_name,
        integrationProfile.authorizedAccountName,
        integrationProfile.external_account_name,
        integrationProfile.externalAccountName,
        integrationProfile.accountName,
        integrationProfile.name,
        mapping?.account_name,
        mapping?.name,
        mapping?.label,
        integration.label,
    ].map(getFeishuReadableDisplayValue).find(Boolean) || 'Feishu Workspace';
};

const resolveGoogleAdsAuthorizedAccountValue = (
    profile: Record<string, any> = {},
    integration: Record<string, any> = {},
    mapping?: Record<string, any>,
) => {
    const integrationProfile = integration.profile_data || {};
    const workspaceExternalAccountName = getNonEmptyDisplayValue(mapping?.external_account_name)
        || getNonEmptyDisplayValue(mapping?.externalAccountName)
        || getNonEmptyDisplayValue(integration.external_account_name)
        || getNonEmptyDisplayValue(integration.externalAccountName);
    if (workspaceExternalAccountName) {
        return workspaceExternalAccountName;
    }

    const authorizedAccountValue = [
        mapping?.authorized_account_email,
        mapping?.authorizedAccountEmail,
        mapping?.external_account_email,
        mapping?.externalAccountEmail,
        mapping?.authorized_account_name,
        mapping?.authorizedAccountName,
        mapping?.external_account_name,
        mapping?.externalAccountName,
        profile.authorized_account_email,
        profile.authorizedAccountEmail,
        profile.external_account_email,
        profile.externalAccountEmail,
        profile.authorized_account_name,
        profile.authorizedAccountName,
        profile.external_account_name,
        profile.externalAccountName,
        profile.email,
        profile.accountEmail,
        profile.loginEmail,
        profile.accountName,
        integrationProfile.authorized_account_email,
        integrationProfile.authorizedAccountEmail,
        integrationProfile.external_account_email,
        integrationProfile.externalAccountEmail,
        integrationProfile.authorized_account_name,
        integrationProfile.authorizedAccountName,
        integrationProfile.external_account_name,
        integrationProfile.externalAccountName,
        integrationProfile.email,
        integrationProfile.accountEmail,
        integrationProfile.loginEmail,
        integrationProfile.accountName,
        integration.external_account_name,
        integration.externalAccountName,
        mapping?.authorized_account_id,
        mapping?.authorizedAccountId,
        mapping?.external_account_id,
        mapping?.externalAccountId,
        integration.external_account_id,
        integration.externalAccountId,
        mapping?.connection_id,
    ].map(getNonEmptyDisplayValue).find(Boolean);

    return authorizedAccountValue || 'Google Ads account';
};

interface IntegrationsSettingsProps {
    workspaceIntegrations: UseWorkspaceIntegrationsReturn;
    mode?: 'onboarding' | 'admin';
    connectProvider?: string | null;
    connectRequestId?: string | null;
}

const IntegrationsSettings: React.FC<IntegrationsSettingsProps> = ({
    workspaceIntegrations: {
        integrations: workspaceIntegrations,
        allIntegrations,
        isLoading,
        refresh: refreshWorkspaceIntegrations,
        connect: connectPlatform,
        disconnect: disconnectPlatform
    }, mode = 'admin',
    connectProvider,
    connectRequestId
}) => {
    const { t, locale } = useTranslation();
    const {
        permissions,
        userBilling,
        currentWorkspace,
    } = useAuth();
    const localizedScenarioLabels: Record<ScenarioFilter, string> = useMemo(() => ({
        Connected: t('website.integrationsSettings.connectedFilter'),
        'Store Ops': t('website.integrationsSettings.storeOps'),
        Advertising: t('website.integrationsSettings.advertising'),
        'Email Marketing': t('website.integrationsSettings.emailMarketing'),
        Analytics: t('website.integrationsSettings.analytics'),
        SEO: t('website.integrationsSettings.seo'),
        'Social Media': t('website.integrationsSettings.socialMedia'),
        Customers: t('website.integrationsSettings.customers'),
        Finance: t('website.integrationsSettings.finance'),
        'Team Collaboration': t('website.integrationsSettings.teamCollaboration'),
        MCP: 'MCP',
    }), [t]);
    const { modal } = useAdminContext();
    const canConnect = mode == 'onboarding' || permissions.checkPermissions([Permission.INTEGRATION_CONNECT]);
    const canDisconnect = mode == 'onboarding' || permissions.checkPermissions([Permission.INTEGRATION_DISCONNECT]);
    const workspacePlanCode = resolveInvitePlanCode({
        currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
        billingSummaryPlanCode: userBilling.summary?.planCode,
        userPlanCode: userBilling.userPlan?.planCode,
    });
    const connectRestrictionCopyContext = resolveConnectRestrictionCopyContext(workspacePlanCode);
    const isSoloWorkspace = mode === 'admin' && isSoloInviteUpgradeRequired(workspacePlanCode);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<ScenarioFilter>(DEFAULT_SCENARIO_FILTER);
    const { onboardingData, updateOnboardingData } = useOnboarding();
    const { showToast, showActionToast } = useToast();
    const { showDialog } = useDialog();

    // 获取各个应用的连接状态
    // 现在统一从 workspace_integrations 表查询
    const {
        shopify: shopifyIntegration,
        bigCommerce: bigCommerceIntegration,
        googleAds: googleAdsIntegration,
        googleAnalytics: googleAnalyticsIntegration,
        tiktokAds: tiktokAdsIntegration,
        googleSearchConsole: googleSearchConsoleIntegration,
        semrush: semrushIntegration,
        slack: slackIntegration,
        woocommerce: woocommerceIntegration,
        magento: magentoIntegration,
        prestashop: prestashopIntegration,
        aliexpress: aliexpressIntegration,
        klaviyo: klaviyoIntegration,
        mailchimp: mailchimpIntegration,
        similarweb: similarwebIntegration,
        ahrefs: ahrefsIntegration,
        omnisend: omnisendIntegration,
        insiderOne: insiderOneIntegration,
        emarsys: emarsysIntegration,
        judgeme: judgemeIntegration,
        wix: wixIntegration,
        metaAds: metaAdsIntegration,
        facebookPage: facebookPageIntegration,
        instagram: instagramIntegration,
        linkedinAds: linkedinAdsIntegration,
        pinterestAds: pinterestAdsIntegration,
        xAds: xAdsIntegration,
        youtube: youtubeIntegration,
        snapchat: snapchatIntegration,
        feishu: feishuIntegration,
        figma: figmaIntegration
    } = workspaceIntegrations;

    const [syncingTools, setSyncingTools] = useState<Set<string>>(new Set());
    const [disconnectingTools, setDisconnectingTools] = useState<Set<string>>(new Set());
    const [disconnectingConnectionIds, setDisconnectingConnectionIds] = useState<Set<string>>(new Set());
    const urlConnectionInFlightRef = useRef(false);
    const connectOperationKeysRef = useRef<Set<string>>(new Set());
    const disconnectOperationKeysRef = useRef<Set<string>>(new Set());
    const completedConnectToolsRef = useRef<Set<string>>(new Set());
    const connectStartConnectionCountsRef = useRef<Map<string, number>>(new Map());
    const connectStartConnectionKeysRef = useRef<Map<string, Set<string>>>(new Map());
    const [connectingTool, setConnectingTool] = useState<string | null>(null);
    const [storeUrl, setStoreUrl] = useState('');
    const [urlConnectionError, setUrlConnectionError] = useState<string | null>(null);
    const [bigCommerceStoreHash, setBigCommerceStoreHash] = useState('');
    const [bigCommerceAccountUuid, setBigCommerceAccountUuid] = useState('');
    const [connectionName, setConnectionName] = useState('');
    const [connectionAlias, setConnectionAlias] = useState('');
    const [apiKeyTool, setApiKeyTool] = useState<string | null>(null);
    const [apiKeyValue, setApiKeyValue] = useState('');
    const [apiKeyAccountLabel, setApiKeyAccountLabel] = useState('');
    const [feishuCustomAppTool, setFeishuCustomAppTool] = useState<string | null>(null);
    const [feishuCustomAppRegion, setFeishuCustomAppRegion] = useState<FeishuCustomAppRegion>('feishu');
    const [feishuCustomAppId, setFeishuCustomAppId] = useState('');
    const [feishuCustomAppSecret, setFeishuCustomAppSecret] = useState('');
    const [prestaShopStoreUrl, setPrestaShopStoreUrl] = useState('');
    const [selectedTool, setSelectedTool] = useState<string | null>(null);
    const [selectedDetailTab, setSelectedDetailTab] = useState<IntegrationDetailTab>('permissions');
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const listScrollTopRef = useRef(0);
    const pendingScrollTargetRef = useRef<'detail' | 'list' | null>(null);
    const [connectionAliasOverrides, setConnectionAliasOverrides] = useState<Record<string, string>>({});
    const [editingLabelConnection, setEditingLabelConnection] = useState<ConnectionData | null>(null);
    const [editingLabelValue, setEditingLabelValue] = useState('');
    const [isAddConnectionUpgradeModalOpen, setIsAddConnectionUpgradeModalOpen] = useState(false);
    const [blockedToolName, setBlockedToolName] = useState<string | null>(null);
    const [storeLimitPrompt, setStoreLimitPrompt] = useState<StoreLimitPrompt | null>(null);
    const [isStoreLimitContactSalesOpen, setIsStoreLimitContactSalesOpen] = useState(false);
    const [integrationPermissionCatalog, setIntegrationPermissionCatalog] = useState<IntegrationPermission[]>(
        () => featureService.getCachedIntegrationPermissions() || []
    );
    const lastStableConnectionsByToolRef = useRef<Record<string, ConnectionData[]>>({});
    const [isSavingConnectionLabel, setIsSavingConnectionLabel] = useState(false);
    const [viewingDetailsConnection, setViewingDetailsConnection] = useState<ConnectionData | null>(null);
    const [googleAdsAccountSelectionConnectionId, setGoogleAdsAccountSelectionConnectionId] = useState<string | null>(null);
    const [mcpConnectors, setMcpConnectors] = useState<McpConnectorResponse[]>([]);
    const [isLoadingMcpConnectors, setIsLoadingMcpConnectors] = useState(false);
    const [mcpConnectorError, setMcpConnectorError] = useState<string | null>(null);
    const [mcpModalMode, setMcpModalMode] = useState<McpConnectorModalMode | null>(null);
    const [isSavingMcpConnector, setIsSavingMcpConnector] = useState(false);
    const [testingMcpConnectorIds, setTestingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const [syncingMcpConnectorIds, setSyncingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const [updatingMcpToolKeys, setUpdatingMcpToolKeys] = useState<Set<string>>(() => new Set());
    const [mcpToolManagerConnectorId, setMcpToolManagerConnectorId] = useState<number | null>(null);
    const [disconnectingMcpConnectorIds, setDisconnectingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const currentMcpWorkspaceIdRef = useRef<string | null>(currentWorkspace?.id || null);
    currentMcpWorkspaceIdRef.current = currentWorkspace?.id || null;
    const mcpListRequestGuardRef = useRef(createWorkspaceRequestGuard(
        () => currentMcpWorkspaceIdRef.current,
    ));

    const openGoogleAdsAccountSelection = useCallback((connectionId?: string | null) => {
        if (!connectionId) {
            return;
        }
        setGoogleAdsAccountSelectionConnectionId(connectionId);
    }, []);

    const closeGoogleAdsAccountSelection = useCallback(() => {
        setGoogleAdsAccountSelectionConnectionId(null);
    }, []);

    const handleGoogleAdsAccountSelectionSaved = useCallback(async () => {
        await refreshWorkspaceIntegrations(false, { forceRefresh: true });
    }, [refreshWorkspaceIntegrations]);

    const loadMcpConnectors = useCallback(async (
        expectedWorkspaceId?: string,
        externalSignal?: AbortSignal,
    ): Promise<boolean> => {
        const workspaceId = expectedWorkspaceId || currentMcpWorkspaceIdRef.current;
        if (!isMcpUiEnabled() || !workspaceId || mode !== 'admin') {
            mcpListRequestGuardRef.current.dispose();
            setMcpConnectors([]);
            setIsLoadingMcpConnectors(false);
            return false;
        }

        const request = mcpListRequestGuardRef.current.begin(workspaceId);
        const abortFromExternalSignal = () => request.abort();
        if (externalSignal?.aborted) {
            request.abort();
        } else {
            externalSignal?.addEventListener('abort', abortFromExternalSignal, { once: true });
        }

        setIsLoadingMcpConnectors(true);
        try {
            const connectors = await mcpConnectorService.list(workspaceId, request.signal);
            if (!request.isCurrent()) {
                const staleRequestError = new Error('Stale MCP connector list response');
                staleRequestError.name = 'AbortError';
                throw staleRequestError;
            }
            setMcpConnectors(connectors || []);
            return true;
        } catch (error) {
            if (!request.isCurrent() || request.signal.aborted) {
                return false;
            }
            console.warn('[IntegrationsSettings] Failed to load MCP connectors', error);
            setMcpConnectorError(t('website.integrationsSettings.mcpLoadFailed'));
            throw error;
        } finally {
            externalSignal?.removeEventListener('abort', abortFromExternalSignal);
            if (request.isLatest()) {
                setIsLoadingMcpConnectors(false);
            }
        }
    }, [mode, t]);

    const mcpFlow = useMcpConnectorFlow({
        workspaceId: currentWorkspace?.id || null,
        refreshCurrentWorkspaceConnectors: async ({ originWorkspaceId, signal }) => {
            const refreshedCurrentWorkspace = await loadMcpConnectors(originWorkspaceId, signal);
            if (!refreshedCurrentWorkspace || signal.aborted || currentMcpWorkspaceIdRef.current !== originWorkspaceId) {
                const staleWorkspaceError = new Error('Workspace changed during MCP refresh');
                staleWorkspaceError.name = 'AbortError';
                throw staleWorkspaceError;
            }
            await refreshWorkspaceIntegrations(false, { forceRefresh: true });
            if (signal.aborted || currentMcpWorkspaceIdRef.current !== originWorkspaceId) {
                const staleWorkspaceError = new Error('Workspace changed during integration refresh');
                staleWorkspaceError.name = 'AbortError';
                throw staleWorkspaceError;
            }
        },
        onFlowError: (reasonCode) => {
            setMcpConnectorError(reasonCode === 'MCP_OAUTH_REFRESH_FAILED'
                ? t('website.integrationsSettings.mcpRefreshFailed')
                : t('website.integrationsSettings.mcpOAuthFailed'));
        },
    });

    useEffect(() => {
        void loadMcpConnectors().catch((error) => {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.warn('[IntegrationsSettings] MCP connector list refresh failed', error);
            }
        });
    }, [currentWorkspace?.id, loadMcpConnectors]);

    useEffect(() => () => {
        mcpListRequestGuardRef.current.dispose();
    }, []);

    const openMcpConnectorModal = useCallback((nextMode: McpConnectorModalMode = 'url') => {
        if (!canConnect) {
            showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
            return;
        }

        setMcpConnectorError(null);
        setMcpModalMode(nextMode);
    }, [canConnect, showToast, t]);

    const closeMcpConnectorModal = useCallback(() => {
        if (!isSavingMcpConnector && mcpFlow.state.kind !== 'creating-draft') {
            setMcpModalMode(null);
            setMcpConnectorError(null);
            mcpFlow.close();
        }
    }, [isSavingMcpConnector, mcpFlow]);

    const handleMcpOAuthStart = useCallback(async (input: NormalizedMcpConnectorInput) => {
        if (!currentWorkspace?.id || isSavingMcpConnector) {
            return;
        }

        setMcpConnectorError(null);
        await mcpFlow.startOAuth(input);
    }, [
        currentWorkspace?.id,
        isSavingMcpConnector,
        mcpFlow,
    ]);

    const handleMcpManualSubmit = useCallback(async (credential: McpCredentialRequest | null) => {
        const workspaceId = currentWorkspace?.id;
        const flowState = mcpFlow.state;
        if (!workspaceId || flowState.kind !== 'manual-setup' || isSavingMcpConnector) {
            return;
        }

        setIsSavingMcpConnector(true);
        setMcpConnectorError(null);
        try {
            if (credential) {
                await mcpConnectorService.setCredential(workspaceId, flowState.connectorId, credential);
            }
            await mcpConnectorService.testConnection(workspaceId, flowState.connectorId);
            await mcpConnectorService.syncTools(workspaceId, flowState.connectorId);
            await loadMcpConnectors();
            await refreshWorkspaceIntegrations(false, { forceRefresh: true });
            setMcpModalMode(null);
            mcpFlow.close();
            showToast(t('website.integrationsSettings.mcpConnectedSuccess'), 'success');
        } catch (error: any) {
            setMcpConnectorError(normalizeConnectionUserMessage(
                error.message,
                t('website.integrationsSettings.mcpSaveFailed')
            ));
        } finally {
            setIsSavingMcpConnector(false);
        }
    }, [
        currentWorkspace?.id,
        isSavingMcpConnector,
        loadMcpConnectors,
        mcpFlow,
        refreshWorkspaceIntegrations,
        showToast,
        t,
    ]);

    useEffect(() => {
        if (mcpFlow.state.kind === 'manual-setup') {
            setMcpModalMode('manual');
        }
        if (mcpFlow.state.kind === 'completed') {
            setMcpModalMode(null);
            showToast(t('website.integrationsSettings.mcpConnectedSuccess'), 'success');
        }
    }, [mcpFlow.state, showToast, t]);

    const handleMcpConnectorTest = useCallback(async (connector: McpConnectorResponse) => {
        const workspaceId = currentWorkspace?.id;
        if (!workspaceId || !canConnect || testingMcpConnectorIds.has(connector.connectorId)) {
            return;
        }

        setTestingMcpConnectorIds(previous => new Set(previous).add(connector.connectorId));
        setMcpConnectorError(null);
        try {
            await mcpConnectorService.testConnection(workspaceId, connector.connectorId);
            await loadMcpConnectors();
            showToast(t('website.integrationsSettings.mcpTestSuccess'), 'success');
        } catch (error: any) {
            const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpTestFailed'));
            setMcpConnectorError(message);
            showToast(message, 'error');
        } finally {
            setTestingMcpConnectorIds(previous => {
                const next = new Set(previous);
                next.delete(connector.connectorId);
                return next;
            });
        }
    }, [canConnect, currentWorkspace?.id, loadMcpConnectors, showToast, t, testingMcpConnectorIds]);

    const handleMcpConnectorSync = useCallback(async (connector: McpConnectorResponse) => {
        const workspaceId = currentWorkspace?.id;
        if (!workspaceId || !canConnect || syncingMcpConnectorIds.has(connector.connectorId)) {
            return;
        }

        setSyncingMcpConnectorIds(previous => new Set(previous).add(connector.connectorId));
        setMcpConnectorError(null);
        try {
            await mcpConnectorService.syncTools(workspaceId, connector.connectorId);
            await loadMcpConnectors();
            showToast(t('website.integrationsSettings.mcpSyncSuccess'), 'success');
        } catch (error: any) {
            const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpSyncFailed'));
            setMcpConnectorError(message);
            showToast(message, 'error');
        } finally {
            setSyncingMcpConnectorIds(previous => {
                const next = new Set(previous);
                next.delete(connector.connectorId);
                return next;
            });
        }
    }, [canConnect, currentWorkspace?.id, loadMcpConnectors, showToast, syncingMcpConnectorIds, t]);

    const handleMcpToolUpdate = useCallback(async (
        connector: McpConnectorResponse,
        tool: McpConnectorTool,
        update: McpConnectorToolUpdateRequest,
    ) => {
        const workspaceId = currentWorkspace?.id;
        const toolId = tool.toolId ?? tool.id;
        if (!workspaceId || toolId == null || !canConnect) {
            showToast(t('website.integrationsSettings.mcpToolUpdateFailed'), 'error');
            return;
        }

        const updateKey = `${connector.connectorId}:${toolId}`;
        if (updatingMcpToolKeys.has(updateKey)) {
            return;
        }

        setUpdatingMcpToolKeys(previous => {
            const next = new Set(previous);
            next.add(updateKey);
            return next;
        });
        setMcpConnectorError(null);
        try {
            await mcpConnectorService.updateTool(
                workspaceId,
                connector.connectorId,
                toolId,
                update,
            );
            await loadMcpConnectors();
            showToast(t('website.integrationsSettings.mcpToolUpdateSuccess'), 'success');
        } catch (error: any) {
            const message = normalizeConnectionUserMessage(
                error.message,
                t('website.integrationsSettings.mcpToolUpdateFailed')
            );
            setMcpConnectorError(message);
            showToast(message, 'error');
        } finally {
            setUpdatingMcpToolKeys(previous => {
                const next = new Set(previous);
                next.delete(updateKey);
                return next;
            });
        }
    }, [
        canConnect,
        currentWorkspace?.id,
        loadMcpConnectors,
        showToast,
        t,
        updatingMcpToolKeys,
    ]);

    const handleMcpConnectorDisconnect = useCallback(async (connector: McpConnectorResponse) => {
        const workspaceId = currentWorkspace?.id;
        if (!workspaceId) {
            return;
        }
        if (!canDisconnect) {
            showToast(t('website.integrationsSettings.disconnectPermissionDenied'), 'error');
            return;
        }

        const disconnectOperationKey = `${MCP_TOOL_NAME}:${connector.connectorId}`;
        if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
            return;
        }
        disconnectOperationKeysRef.current.add(disconnectOperationKey);
        const confirmed = await showDialog({
            title: t('website.integrationsSettings.disconnectTitle', { target: connector.displayName }),
            subtitle: t('website.integrationsSettings.disconnectSubtitle'),
            confirmText: t('website.integrationsSettings.disconnect'),
            cancelText: t('common.cancel'),
            destructive: true,
        });

        if (!confirmed) {
            disconnectOperationKeysRef.current.delete(disconnectOperationKey);
            return;
        }

        setDisconnectingMcpConnectorIds(previous => {
            const next = new Set(previous);
            next.add(connector.connectorId);
            return next;
        });
        setMcpConnectorError(null);
        try {
            await mcpConnectorService.disconnect(workspaceId, connector.connectorId);
            setMcpToolManagerConnectorId(previous => (
                previous === connector.connectorId ? null : previous
            ));
            await loadMcpConnectors();
            void refreshWorkspaceIntegrations(false, { forceRefresh: true }).catch(error => {
                console.warn('[IntegrationsSettings] Failed to refresh integrations after MCP disconnect', error);
            });
            showToast(t('website.integrationsSettings.disconnectedSuccess', { tool: connector.displayName }), 'success');
        } catch (error: any) {
            const message = `${t('website.integrationsSettings.disconnectFailed')}: ${normalizeConnectionUserMessage(
                error.message,
                t('website.integrationsSettings.failedToDisconnectFromTool', { tool: connector.displayName })
            )}`;
            setMcpConnectorError(message);
            showToast(message, 'error');
        } finally {
            disconnectOperationKeysRef.current.delete(disconnectOperationKey);
            setDisconnectingMcpConnectorIds(previous => {
                const next = new Set(previous);
                next.delete(connector.connectorId);
                return next;
            });
        }
    }, [
        canDisconnect,
        currentWorkspace?.id,
        loadMcpConnectors,
        refreshWorkspaceIntegrations,
        showDialog,
        showToast,
        t,
    ]);

    useEffect(() => {
        if (mode !== 'admin') {
            return undefined;
        }

        let isMounted = true;
        const timingTracker = createIntegrationsSettingsTimingTracker(currentWorkspace?.id);

        trackIntegrationsSettingsTiming(timingTracker, 'integrationPermissions', featureService.getIntegrationPermissions())
            .then((permissions) => {
                if (!isMounted) {
                    return;
                }
                setIntegrationPermissionCatalog(permissions || []);
            })
            .catch((error) => {
                console.warn('[IntegrationsSettings] Failed to load integration permission catalog', error);
            })
            .finally(() => {
                console.info('[IntegrationsSettings] integration permission load timings', timingTracker.toLogPayload());
            });

        return () => {
            isMounted = false;
        };
    }, [mode]);

    const getScrollContainer = useCallback(() => {
        if (scrollContainerRef.current?.isConnected) {
            return scrollContainerRef.current;
        }

        const nextScrollContainer = findNearestScrollContainer(rootRef.current);
        scrollContainerRef.current = nextScrollContainer;
        return nextScrollContainer;
    }, []);

    const closeIntegrationDetail = useCallback(() => {
        pendingScrollTargetRef.current = 'list';
        setSelectedTool(null);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        searchInputRef.current?.focus();
    }, []);

    const openEditConnectionLabelModal = (connection: ConnectionData) => {
        setEditingLabelConnection(connection);
        setEditingLabelValue(connection.alias || '');
    };

    const closeEditConnectionLabelModal = () => {
        setEditingLabelConnection(null);
        setEditingLabelValue('');
    };

    const handleSaveConnectionLabel = async () => {
        if (!editingLabelConnection || isSavingConnectionLabel) {
            return;
        }

        const connection = editingLabelConnection;
        const nextLabel = editingLabelValue.trim();
        const integrationId = Number(connection.id);

        setIsSavingConnectionLabel(true);
        try {
            if (Number.isFinite(integrationId) && currentWorkspace?.id) {
                await authService.updateWorkspaceIntegrationLabel(integrationId, {
                    label: nextLabel || null,
                    axiosRequestConfig: {
                        headers: {
                            'X-Workspace-Id': currentWorkspace.id,
                        },
                    },
                });
            }

            setConnectionAliasOverrides(prev => ({
                ...prev,
                [connection.id]: nextLabel,
            }));

            closeEditConnectionLabelModal();
            void refreshWorkspaceIntegrations(false).catch(error => {
                console.warn('[IntegrationsSettings] Failed to refresh integrations after label update', error);
            });
        } catch (error: any) {
            showToast(
                `${t('website.integrationsSettings.labelUpdateFailed')}: ${error.message || t('website.integrationsSettings.failedToUpdateIntegrationLabel')}`,
                'error'
            );
        } finally {
            setIsSavingConnectionLabel(false);
        }
    };

    const handleOpenAddConnectionUpgrade = () => {
        setIsAddConnectionUpgradeModalOpen(true);
    };

    const handleOpenAddConnectionUpgradePricing = () => {
        setIsAddConnectionUpgradeModalOpen(false);
        modal.openModal('pricing');
    };

    const handleStoreLimitPrimaryAction = () => {
        const upgradeTarget = storeLimitPrompt?.upgradeTarget;
        setStoreLimitPrompt(null);

        if (upgradeTarget === 'SALES') {
            setIsStoreLimitContactSalesOpen(true);
            return;
        }

        modal.openModal('pricing');
    };

    // 需要输入 URL 的应用配置
    const URL_INPUT_CONFIG: Record<string, { prefix: string, suffix: string, placeholder: string, fieldLabel?: string, helperText?: string }> = {
        'Shopify': {
            prefix: 'https://', suffix: '.myshopify.com', placeholder: 'your-store'
        },
        'BigCommerce': {
            prefix: 'https://store-', suffix: '.mybigcommerce.com', placeholder: 'your-store'
        },
    };

    const resetConnectionForm = useCallback(() => {
        setConnectingTool(null);
        setStoreUrl('');
        setUrlConnectionError(null);
        setBigCommerceStoreHash('');
        setBigCommerceAccountUuid('');
        setConnectionName('');
        setConnectionAlias('');
        setApiKeyTool(null);
        setApiKeyValue('');
        setApiKeyAccountLabel('');
        setFeishuCustomAppTool(null);
        setFeishuCustomAppRegion('feishu');
        setFeishuCustomAppId('');
        setFeishuCustomAppSecret('');
        setPrestaShopStoreUrl('');
    }, []);

    // 检查应用是否支持连接
    const isSupported = (name: string) => isConnectableIntegrationTool(name);

    const getWorkspaceConnectionCount = useCallback((tool: string) => {
        const targetKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool));

        if (!targetKey) {
            return 0;
        }

        return allIntegrations.reduce((count, integration) => {
            if (!integration.connected || normalizeIntegrationProvider(integration.provider) !== targetKey) {
                return count;
            }

            const mappings = integration.mappings || [];
            if (mappings.length === 0) {
                return count + 1;
            }

            return count + mappings.filter(mapping => {
                const status = String(mapping?.status || 'connected').toLowerCase();
                return status === 'active' || status === 'connected';
            }).length;
        }, 0);
    }, [allIntegrations]);

    const getWorkspaceConnectionIdentityKeys = useCallback((tool: string) => {
        const providerKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool) || tool);

        if (!providerKey) {
            return new Set<string>();
        }

        return allIntegrations.reduce((keys, integration) => {
            if (normalizeIntegrationProvider(integration.provider) !== providerKey) {
                return keys;
            }

            const mappings = integration.mappings?.length ? integration.mappings : [undefined];
            mappings.forEach((mapping, idx) => {
                keys.add(String(mapping?.id || mapping?.connection_id || `${integration.provider}-${idx}`));
            });

            return keys;
        }, new Set<string>());
    }, [allIntegrations]);

    const clearConnectionSuccessTracking = useCallback((tool: string) => {
        connectStartConnectionCountsRef.current.delete(tool);
        connectStartConnectionKeysRef.current.delete(tool);
    }, []);

    const startSyncingTool = useCallback((tool: string) => {
        completedConnectToolsRef.current.delete(tool);
        connectStartConnectionCountsRef.current.set(tool, getWorkspaceConnectionCount(tool));
        connectStartConnectionKeysRef.current.set(tool, getWorkspaceConnectionIdentityKeys(tool));
        setSyncingTools(prev => new Set(prev).add(tool));
    }, [getWorkspaceConnectionCount, getWorkspaceConnectionIdentityKeys]);

    const clearSyncingTool = useCallback((tool: string) => {
        setSyncingTools(prev => {
            if (!prev.has(tool)) {
                return prev;
            }

            const next = new Set(prev);
            next.delete(tool);
            return next;
        });
    }, []);

    const completeConnectionSuccess = useCallback((tool: string) => {
        if (!completedConnectToolsRef.current.has(tool)) {
            completedConnectToolsRef.current.add(tool);
            setSelectedDetailTab('connections');
            clearConnectionSuccessTracking(tool);

            if (updateOnboardingData) {
                const connectedIntegrations = onboardingData.connectedIntegrations || [];
                updateOnboardingData({
                    connectedIntegrations: connectedIntegrations.includes(tool)
                        ? connectedIntegrations
                        : [...connectedIntegrations, tool],
                });
            }

            showToast(t('website.integrationsSettings.connectedSuccess', { tool }), 'success');
        }

        clearSyncingTool(tool);
    }, [
        clearSyncingTool,
        clearConnectionSuccessTracking,
        onboardingData.connectedIntegrations,
        showToast,
        updateOnboardingData,
    ]);

    const refreshIntegrationsAfterConnectionSuccess = useCallback((tool: string) => {
        void refreshWorkspaceIntegrations(false).catch(error => {
            console.warn(`[IntegrationsSettings] Failed to refresh integrations after ${tool} connection`, error);
        });
    }, [refreshWorkspaceIntegrations]);

    /**
     * 获取应用的连接状态
     * 统一处理所有应用的连接状态判断逻辑
     * 现在从 workspace_integrations 表查询，检查对象的 connected 字段
     */
    const getWorkspaceConnectionStatus = useCallback((tool: string): 'idle' | 'connected' => {
        const provider = getIntegrationProviderForTool(tool);

        switch (provider) {
            case 'shopify':
                return shopifyIntegration?.connected ? 'connected' : 'idle';
            case 'bigcommerce':
                return bigCommerceIntegration?.connected ? 'connected' : 'idle';
            case 'google-ads':
                return googleAdsIntegration?.connected ? 'connected' : 'idle';
            case 'google-analytics':
                return googleAnalyticsIntegration?.connected ? 'connected' : 'idle';
            case 'tiktok-ads':
                return tiktokAdsIntegration?.connected ? 'connected' : 'idle';
            case 'google-search-console':
                return googleSearchConsoleIntegration?.connected ? 'connected' : 'idle';
            case 'semrush':
                return semrushIntegration?.connected ? 'connected' : 'idle';
            case 'slack':
                return slackIntegration?.connected ? 'connected' : 'idle';
            case 'woocommerce':
                return woocommerceIntegration?.connected ? 'connected' : 'idle';
            case 'magento':
                return magentoIntegration?.connected ? 'connected' : 'idle';
            case 'prestashop':
                return prestashopIntegration?.connected ? 'connected' : 'idle';
            case 'aliexpress':
                return aliexpressIntegration?.connected ? 'connected' : 'idle';
            case 'klaviyo':
                return klaviyoIntegration?.connected ? 'connected' : 'idle';
            case 'mailchimp':
                return mailchimpIntegration?.connected ? 'connected' : 'idle';
            case 'similarweb':
                return similarwebIntegration?.connected ? 'connected' : 'idle';
            case 'ahrefs':
                return ahrefsIntegration?.connected ? 'connected' : 'idle';
            case 'omnisend':
                return omnisendIntegration?.connected ? 'connected' : 'idle';
            case 'insider-one':
                return insiderOneIntegration?.connected ? 'connected' : 'idle';
            case 'emarsys':
                return emarsysIntegration?.connected ? 'connected' : 'idle';
            case 'judgeme':
                return judgemeIntegration?.connected ? 'connected' : 'idle';
            case 'wix':
                return wixIntegration?.connected ? 'connected' : 'idle';
            case 'meta-ads':
                return metaAdsIntegration?.connected ? 'connected' : 'idle';
            case 'facebook-page':
                return facebookPageIntegration?.connected ? 'connected' : 'idle';
            case 'instagram':
                return instagramIntegration?.connected ? 'connected' : 'idle';
            case 'linkedin-ads':
                return linkedinAdsIntegration?.connected ? 'connected' : 'idle';
            case 'pinterest-ads':
                return pinterestAdsIntegration?.connected ? 'connected' : 'idle';
            case 'x-ads':
                return xAdsIntegration?.connected ? 'connected' : 'idle';
            case 'youtube-ads':
                return youtubeIntegration?.connected ? 'connected' : 'idle';
            case 'snapchat-ads':
                return snapchatIntegration?.connected ? 'connected' : 'idle';
            case 'feishu':
                return feishuIntegration?.connected ? 'connected' : 'idle';
            case 'figma':
                return figmaIntegration?.connected ? 'connected' : 'idle';
            default:
                return 'idle';
        }
    }, [
        aliexpressIntegration?.connected,
        ahrefsIntegration?.connected,
        bigCommerceIntegration?.connected,
        facebookPageIntegration?.connected,
        feishuIntegration?.connected,
        emarsysIntegration?.connected,
        figmaIntegration?.connected,
        googleAdsIntegration?.connected,
        googleAnalyticsIntegration?.connected,
        googleSearchConsoleIntegration?.connected,
        instagramIntegration?.connected,
        insiderOneIntegration?.connected,
        judgemeIntegration?.connected,
        klaviyoIntegration?.connected,
        linkedinAdsIntegration?.connected,
        magentoIntegration?.connected,
        mailchimpIntegration?.connected,
        metaAdsIntegration?.connected,
        omnisendIntegration?.connected,
        pinterestAdsIntegration?.connected,
        prestashopIntegration?.connected,
        semrushIntegration?.connected,
        shopifyIntegration?.connected,
        similarwebIntegration?.connected,
        slackIntegration?.connected,
        snapchatIntegration?.connected,
        tiktokAdsIntegration?.connected,
        wixIntegration?.connected,
        woocommerceIntegration?.connected,
        xAdsIntegration?.connected,
        youtubeIntegration?.connected,
    ]);

    const getConnectionStatus = (tool: string): 'idle' | 'syncing' | 'connected' => {
        const workspaceStatus = getWorkspaceConnectionStatus(tool);
        if (workspaceStatus === 'connected') return 'connected';
        if (syncingTools.has(tool)) return 'syncing';
        return 'idle';
    };

    const getRealConnectionsCount = (tool: string): number => {
        const providerKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool) || tool);
        return allIntegrations.filter(integration => normalizeIntegrationProvider(integration.provider) === providerKey).length;
    };

    const isToolConnectedBackend = (tool: string): boolean => {
        const provider = getIntegrationProviderForTool(tool);

        switch (provider) {
            case 'shopify':
                return !!shopifyIntegration?.connected;
            case 'bigcommerce':
                return !!bigCommerceIntegration?.connected;
            case 'google-ads':
                return !!googleAdsIntegration?.connected;
            case 'google-analytics':
                return !!googleAnalyticsIntegration?.connected;
            case 'tiktok-ads':
                return !!tiktokAdsIntegration?.connected;
            case 'google-search-console':
                return !!googleSearchConsoleIntegration?.connected;
            case 'semrush':
                return !!semrushIntegration?.connected;
            case 'slack':
                return !!slackIntegration?.connected;
            case 'woocommerce':
                return !!woocommerceIntegration?.connected;
            case 'magento':
                return !!magentoIntegration?.connected;
            case 'prestashop':
                return !!prestashopIntegration?.connected;
            case 'aliexpress':
                return !!aliexpressIntegration?.connected;
            case 'klaviyo':
                return !!klaviyoIntegration?.connected;
            case 'mailchimp':
                return !!mailchimpIntegration?.connected;
            case 'similarweb':
                return !!similarwebIntegration?.connected;
            case 'ahrefs':
                return !!ahrefsIntegration?.connected;
            case 'omnisend':
                return !!omnisendIntegration?.connected;
            case 'insider-one':
                return !!insiderOneIntegration?.connected;
            case 'emarsys':
                return !!emarsysIntegration?.connected;
            case 'judgeme':
                return !!judgemeIntegration?.connected;
            case 'wix':
                return !!wixIntegration?.connected;
            case 'meta-ads':
                return !!metaAdsIntegration?.connected;
            case 'facebook-page':
                return !!facebookPageIntegration?.connected;
            case 'instagram':
                return !!instagramIntegration?.connected;
            case 'feishu':
                return !!feishuIntegration?.connected;
            case 'figma':
                return !!figmaIntegration?.connected;
            default:
                return false;
        }
    };

    useEffect(() => {
        Array.from(syncingTools).forEach(tool => {
            if (disconnectingTools.has(tool)) {
                return;
            }

            const startConnectionCount = connectStartConnectionCountsRef.current.get(tool) ?? 0;
            const currentConnectionCount = getWorkspaceConnectionCount(tool);
            const hasNewConnection = currentConnectionCount > startConnectionCount;
            const hasCompletedSuccess = completedConnectToolsRef.current.has(tool);
            const workspaceStatus = getWorkspaceConnectionStatus(tool);

            if (workspaceStatus === 'connected' && (hasNewConnection || hasCompletedSuccess)) {
                completeConnectionSuccess(tool);
            }
        });
    }, [completeConnectionSuccess, disconnectingTools, getWorkspaceConnectionCount, getWorkspaceConnectionStatus, syncingTools]);

    useEffect(() => {
        if (!connectProvider) {
            return;
        }
        if (isLoading) {
            return;
        }

        const tool = getToolFromConnectProvider(connectProvider);
        if (!tool) {
            showToast(t('website.integrationsSettings.unsupportedIntegration', { tool: connectProvider }), 'error');
            return;
        }

        const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
        if (!isSupported(normalizedTool)) {
            showToast(t('website.integrationsSettings.unsupportedIntegration', { tool }), 'error');
            return;
        }

        if (URL_INPUT_CONFIG[tool]) {
            setConnectingTool(tool);
            setStoreUrl('');
            setBigCommerceStoreHash('');
            setBigCommerceAccountUuid('');
            return;
        }

        if (MANUAL_CREDENTIAL_TOOLS.has(tool)) {
            setApiKeyTool(tool);
            setApiKeyValue('');
            setApiKeyAccountLabel('');
            setPrestaShopStoreUrl('');
            return;
        }

        if (tool === 'Feishu') {
            setFeishuCustomAppTool(tool);
            setFeishuCustomAppRegion('feishu');
            setFeishuCustomAppId('');
            setFeishuCustomAppSecret('');
            return;
        }

        resetConnectionForm();
        void handleConnect(tool);
    }, [connectProvider, connectRequestId, isLoading]);

    /**
     * 处理应用连接
     * 根据 app 类型选择不同的连接方式：
     * - 需要输入 URL 的应用（Shopify, BigCommerce）：打开 URL 输入弹窗
     * - OAuth 应用（Google Ads, WooCommerce, Magento）：直接调用 OAuth 连接
     */
    const handleConnect = async (tool: string) => {
        if (isSoloWorkspace && (tool === 'Feishu' || tool === 'Slack')) {
            setBlockedToolName(tool);
            return;
        }

        if (!canConnect) {
            showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
            return;
        }

        if (isMcpTool(tool) && !isMcpUiEnabled()) {
            return;
        }

        if (isMcpTool(tool)) {
            setSelectedTool(MCP_TOOL_NAME);
            openMcpConnectorModal('url');
            return;
        }

        if (!isSupported(tool)) return;

        if (URL_INPUT_CONFIG[tool]) {
            setConnectingTool(tool);
            setStoreUrl('');
            setBigCommerceStoreHash('');
            setBigCommerceAccountUuid('');
            setConnectionName('');
            setConnectionAlias('');
            return;
        }

        if (MANUAL_CREDENTIAL_TOOLS.has(tool)) {
            setApiKeyTool(tool);
            setApiKeyValue('');
            setApiKeyAccountLabel('');
            setPrestaShopStoreUrl('');
            return;
        }

        if (tool === 'Feishu') {
            setFeishuCustomAppTool(tool);
            setFeishuCustomAppRegion('feishu');
            setFeishuCustomAppId('');
            setFeishuCustomAppSecret('');
            return;
        }

        if (connectOperationKeysRef.current.has(tool)) {
            return;
        }
        connectOperationKeysRef.current.add(tool);

        try {
            startSyncingTool(tool);

            const providerKey = getIntegrationProviderForTool(tool) || tool;
            const result = await withZernioConnectTimeout(
                tool,
                connectPlatform(providerKey, { connection_scene: mode }, {
                    onSuccess: async () => {
                        completeConnectionSuccess(tool);
                        refreshIntegrationsAfterConnectionSuccess(tool);
                    },
                    onError: (errorMsg: string) => {
                        if (!isDirectConnectDialogClosed(errorMsg)) {
                            showToast(`${t('website.integrationsSettings.connectionFailed')}: ${normalizeConnectionUserMessage(errorMsg)}`, 'error');
                        }
                        clearSyncingTool(tool);
                        clearConnectionSuccessTracking(tool);
                    },
                    onRestriction: async (restriction) => {
                        await handleConnectRestriction(restriction);
                        clearSyncingTool(tool);
                        clearConnectionSuccessTracking(tool);
                    }
                })
            );

            if (await handleConnectRestriction(result)) {
                clearSyncingTool(tool);
                clearConnectionSuccessTracking(tool);
                return;
            }

            if (result?.requiresAccountSelection) {
                openGoogleAdsAccountSelection(result.connectionId);
                clearSyncingTool(tool);
                clearConnectionSuccessTracking(tool);
                return;
            }

            if (!result?.uiOpened) {
                completeConnectionSuccess(tool);
                refreshIntegrationsAfterConnectionSuccess(tool);
            }
        } catch (error: any) {
            showToast(normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.failedToConnectToTool', { tool })), 'error');
            clearSyncingTool(tool);
            clearConnectionSuccessTracking(tool);
        } finally {
            connectOperationKeysRef.current.delete(tool);
        }
    };

    const handleApiKeyFallbackConnection = (tool: string) => {
        if (!canConnect) {
            showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
            return;
        }

        setApiKeyTool(tool);
        setApiKeyValue('');
        setApiKeyAccountLabel('');
    };

    const handleFeishuCustomAppConnection = async () => {
        const tool = feishuCustomAppTool;
        const appId = feishuCustomAppId.trim();
        const appSecret = feishuCustomAppSecret.trim();
        const scope = FEISHU_REQUIRED_BASE_SCOPE;
        const redirectUri = getFeishuOAuthRedirectUri();
        if (!tool || !appId || !appSecret || !redirectUri) {
            return;
        }

        try {
            startSyncingTool(tool);
            const result = await connectPlatform('feishu', {
                region: feishuCustomAppRegion,
                provider: feishuCustomAppRegion,
                app_id: appId,
                app_secret: appSecret,
                scope,
                redirect_uri: redirectUri,
                connection_scene: mode,
            });

            if (await handleConnectRestriction(result)) {
                clearConnectionSuccessTracking(tool);
                return;
            }

            completeConnectionSuccess(tool);
            resetConnectionForm();
            refreshIntegrationsAfterConnectionSuccess(tool);
        } catch (error: any) {
            showToast(`Connection Failed: ${normalizeConnectionUserMessage(error.message, `Failed to connect to ${tool}`)}`, 'error');
            clearConnectionSuccessTracking(tool);
        } finally {
            clearSyncingTool(tool);
        }
    };

    const handleApiKeyConnection = async () => {
        const tool = apiKeyTool;
        const isPrestaShop = tool === 'PrestaShop';
        const apiKey = apiKeyValue.trim();
        const storeUrlValue = prestaShopStoreUrl.trim();
        if (!tool || (!isPrestaShop && !apiKey)) {
            return;
        }
        if (isPrestaShop && (!storeUrlValue || !apiKey)) {
            return;
        }

        try {
            startSyncingTool(tool);
            const result = await connectPlatform(
                API_KEY_CONNECT_PROVIDER[tool] || tool,
                isPrestaShop
                    ? {
                        storeUrl: storeUrlValue,
                        apiKey,
                        accountLabel: apiKeyAccountLabel.trim(),
                        connection_scene: mode,
                    }
                    : {
                        apiKey,
                        accountLabel: apiKeyAccountLabel.trim(),
                        connection_scene: mode,
                    }
            );

            if (await handleConnectRestriction(result)) {
                resetConnectionForm();
                clearConnectionSuccessTracking(tool);
                return;
            }

            completeConnectionSuccess(tool);
            resetConnectionForm();
            refreshIntegrationsAfterConnectionSuccess(tool);
        } catch (error: any) {
            showToast(`${t('website.integrationsSettings.connectionFailed')}: ${normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.failedToConnectToTool', { tool }))}`, 'error');
            clearConnectionSuccessTracking(tool);
        } finally {
            clearSyncingTool(tool);
        }
    };

    const handleRegister = (tool: string) => {
        const url = REGISTRATION_URLS[tool] || `https://www.google.com/search?q=${encodeURIComponent(tool)}+signup`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleConnectRestriction = async (payload?: {
        restrictionState?: StoreTrialEligibilityState | null;
        eligibilityState?: StoreTrialEligibilityState | null;
        blockConnect?: boolean | null;
        upgradeRequired?: boolean | null;
        message?: string | null;
        eligibilityMessage?: string | null;
        currentStoreCount?: number | null;
        storeLimit?: number | null;
        upgradeTarget?: 'TEAM' | 'SALES' | null;
    }) => {
        const action = getConnectRestrictionAction(payload, t, connectRestrictionCopyContext);

        if (!action.shouldBlockConnect) {
            return false;
        }

        if (action.shouldShowStoreLimitPrompt && action.storeLimitPrompt) {
            setStoreLimitPrompt(action.storeLimitPrompt);
            return true;
        }

        if (action.shouldShowSubscriptionPrompt) {
            showActionToast({
                title: t('website.integrationsSettings.currentTrialCantBeUsedHere'),
                description: t('website.integrationsSettings.currentTrialCantBeUsedHereDescription'),
                type: 'warning',
                duration: Infinity,
                action: [
                    {
                        label: t('website.workspaceSwitcher.startSubscription'),
                        onClick: () => modal.openModal('pricing'),
                    },
                    {
                        label: t('website.integrationsSettings.tryAnotherApp'),
                        variant: 'secondary',
                        onClick: () => {},
                    },
                ],
            });
            return true;
        }

        showToast(action.message || t('website.integrationsSettings.storeBoundToAnotherWorkspace'), 'error');
        return true;
    };

    /**
     * 处理需要输入 URL 的应用连接
     * 包括 Shopify 和 BigCommerce
     */
    const handleUrlConnection = async () => {
        if (!connectingTool || !storeUrl.trim()) return;
        if (connectingTool === 'BigCommerce' && (!bigCommerceStoreHash.trim() || !bigCommerceAccountUuid.trim())) {
            return;
        }
        if (urlConnectionInFlightRef.current) {
            return;
        }

        const activeConnectingTool = connectingTool;
        let keepSyncingUntilCallback = false;
        urlConnectionInFlightRef.current = true;

        try {
            startSyncingTool(activeConnectingTool);
            setUrlConnectionError(null);

            if (activeConnectingTool === 'Shopify') {
                // Shopify connection logic
                const shopDomain = normalizeShopifyStoreDomainInput(storeUrl);
                const url = new URL(window.location.href);
                url.searchParams.set('connect_success', 'shopify');
                const result = await connectPlatform('shopify', {
                    shop_domain: shopDomain,
                    redirect_url: url.toString(),
                    connection_scene: mode,
                    connection_name: connectionName.trim() || shopDomain,
                    connection_alias: connectionAlias.trim(),
                }, {
                    onAuthorizationStarted: () => {
                        resetConnectionForm();
                    },
                });
                if (await handleConnectRestriction(result)) {
                    resetConnectionForm();
                    clearConnectionSuccessTracking(activeConnectingTool);
                    return;
                }
                if (result?.action === 'REDIRECT' && result?.url) {
                    window.location.href = result.url;
                    return;
                }
                completeConnectionSuccess(activeConnectingTool);
                refreshIntegrationsAfterConnectionSuccess(activeConnectingTool);
            } else if (activeConnectingTool === 'BigCommerce') {
                // BigCommerce connection logic
                const bigCommerceStoreDomain = `store-${storeUrl}.mybigcommerce.com`;
                const result = await connectPlatform('bigcommerce', {
                    store_domain: `https://${bigCommerceStoreDomain}/`,
                    store_hash: bigCommerceStoreHash.trim(),
                    accountUuid: bigCommerceAccountUuid.trim(),
                    connection_scene: mode,
                    connection_name: connectionName.trim() || bigCommerceStoreDomain,
                    connection_alias: connectionAlias.trim(),
                }, {
                    onAuthorizationStarted: () => {
                        resetConnectionForm();
                    },
                });
                if (await handleConnectRestriction(result)) {
                    resetConnectionForm();
                    clearConnectionSuccessTracking(activeConnectingTool);
                    return;
                }
                if (result?.action === 'REDIRECT' && result?.url) {
                    window.location.href = result.url;
                    return;
                }
                completeConnectionSuccess(activeConnectingTool);
            } else {
                // Non-URL tools (OAuth based, etc.)
                const result = await connectPlatform(activeConnectingTool, {
                    connection_scene: mode,
                    connection_name: connectionName.trim(),
                    connection_alias: connectionAlias.trim(),
                }, {
                    onSuccess: async () => {
                        completeConnectionSuccess(activeConnectingTool);
                        resetConnectionForm();
                        refreshIntegrationsAfterConnectionSuccess(activeConnectingTool);
                    },
                    onError: (errorMsg: string) => {
                        const normalizedError = normalizeConnectionUserMessage(errorMsg);
                        setUrlConnectionError(normalizedError);
                        if (!isDirectConnectDialogClosed(errorMsg)) {
                            showToast(`Connection Failed: ${normalizedError}`, 'error');
                        }
                        clearConnectionSuccessTracking(activeConnectingTool);
                    },
                    onRestriction: async (restriction) => {
                        await handleConnectRestriction(restriction);
                        clearConnectionSuccessTracking(activeConnectingTool);
                    }
                });

                if (await handleConnectRestriction(result)) {
                    resetConnectionForm();
                    clearConnectionSuccessTracking(activeConnectingTool);
                    return;
                }

                if (result?.uiOpened) {
                    keepSyncingUntilCallback = true;
                } else if (!result?.uiOpened && !result?.action) {
                    completeConnectionSuccess(activeConnectingTool);
                    resetConnectionForm();
                    refreshIntegrationsAfterConnectionSuccess(activeConnectingTool);
                }
            }

            if (!keepSyncingUntilCallback) {
                resetConnectionForm();
            }
        } catch (error: any) {
            const normalizedError = normalizeConnectionUserMessage(error.message, `Failed to connect to ${activeConnectingTool}`);
            setUrlConnectionError(normalizedError);
            showToast(`Connection Failed: ${normalizedError}`, 'error');
            clearConnectionSuccessTracking(activeConnectingTool);
        } finally {
            urlConnectionInFlightRef.current = false;
            if (keepSyncingUntilCallback) {
                return;
            }
            clearSyncingTool(activeConnectingTool);
        }
    };

    const getConnectionDisconnectTrackingId = (tool: string, connection?: ConnectionData) =>
        tool === 'Feishu'
            ? (connection?.slugId || connection?.connectionId || connection?.id)
            : (connection?.connectionId || connection?.id);

    const getDisconnectOperationKey = (tool: string, connection?: ConnectionData) =>
        `${tool}:${getConnectionDisconnectTrackingId(tool, connection) || 'tool'}`;

    const startDisconnectFeedback = (tool: string, connection?: ConnectionData) => {
        const connectionId = getConnectionDisconnectTrackingId(tool, connection);
        setDisconnectingTools(prev => new Set(prev).add(tool));
        if (connectionId) {
            setDisconnectingConnectionIds(prev => new Set(prev).add(connectionId));
        }
        return connectionId;
    };

    const clearDisconnectFeedback = (tool: string, connectionId?: string) => {
        setDisconnectingTools(prev => {
            const next = new Set(prev);
            next.delete(tool);
            return next;
        });
        if (connectionId) {
            setDisconnectingConnectionIds(prev => {
                const next = new Set(prev);
                next.delete(connectionId);
                return next;
            });
        }
    };

    /**
     * 处理断开集成连接 - 使用 showDialog 显示确认弹窗
     */
    const handleDisconnect = async (tool: string, connection?: ConnectionData) => {
        if (!canDisconnect) {
            showToast('Permission Denied: You do not have permission to disconnect integrations', 'error');
            return;
        }

        const disconnectOperationKey = getDisconnectOperationKey(tool, connection);
        if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
            return;
        }
        disconnectOperationKeysRef.current.add(disconnectOperationKey);
        const confirmed = await showDialog({
            title: t('website.integrationsSettings.disconnectTitle', { target: tool }),
            subtitle: t('website.integrationsSettings.disconnectSubtitle'),
            confirmText: t('website.integrationsSettings.disconnect'),
            cancelText: t('common.cancel'),
            destructive: true,
        });

        if (confirmed) {
            await performDisconnect(tool, connection, disconnectOperationKey);
            return;
        }

        disconnectOperationKeysRef.current.delete(disconnectOperationKey);
    };

    const handleConnectionDisconnect = async (tool: string, connection: ConnectionData) => {
        if (!canDisconnect) {
            showToast(t('website.integrationsSettings.disconnectPermissionDenied'), 'error');
            return;
        }

        const disconnectOperationKey = getDisconnectOperationKey(tool, connection);
        if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
            return;
        }
        disconnectOperationKeysRef.current.add(disconnectOperationKey);
        const connectionName = connection.accountId || connection.name || tool;
        const confirmed = await showDialog({
            title: t('website.integrationsSettings.disconnectTitle', { target: connectionName }),
            subtitle: t('website.integrationsSettings.disconnectSubtitle'),
            confirmText: t('website.integrationsSettings.disconnect'),
            cancelText: t('common.cancel'),
            destructive: true,
        });

        if (confirmed) {
            await performDisconnect(tool, connection, disconnectOperationKey);
            return;
        }

        disconnectOperationKeysRef.current.delete(disconnectOperationKey);
    };

    /**
     * 执行断开集成连接操作
     * 使用统一的 disconnect API
     */
    const performDisconnect = async (tool: string, connection?: ConnectionData, disconnectOperationKey?: string) => {
        clearConnectionSuccessTracking(tool);
        setSyncingTools(prev => new Set(prev).add(tool));
        setDisconnectingTools(prev => new Set(prev).add(tool));
        const connectionId = getConnectionDisconnectTrackingId(tool, connection);
        if (connectionId) {
            setDisconnectingConnectionIds(prev => new Set(prev).add(connectionId));
        }

        try {
            const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
            const workspaceIntegrationId = connection?.workspaceIntegrationId || connection?.id;
            const shopDomain = tool === 'Shopify'
                ? (connection?.accountId || connection?.name)
                : undefined;

            // 使用统一的 disconnect API
            await disconnectPlatform(normalizedTool, {
                connectionId,
                workspaceIntegrationId,
                shopDomain,
                authType: connection?.authType,
                native: connection?.native,
            });

            // Update onboarding data
            if (updateOnboardingData) {
                updateOnboardingData({connectedIntegrations: (onboardingData.connectedIntegrations || []).filter(i => i !== tool)});
            }

            showToast(t('website.integrationsSettings.disconnectedSuccess', { tool }), 'success');
        } catch (error: any) {
            showToast(`${t('website.integrationsSettings.disconnectFailed')}: ${normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.failedToDisconnectFromTool', { tool }))}`, 'error');
        } finally {
            if (disconnectOperationKey) {
                disconnectOperationKeysRef.current.delete(disconnectOperationKey);
            }
            setSyncingTools(prev => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
            setDisconnectingTools(prev => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
            if (connectionId) {
                setDisconnectingConnectionIds(prev => {
                    const next = new Set(prev);
                    next.delete(connectionId);
                    return next;
                });
            }
        }
    };

    const getMcpConnectorConnections = useCallback((): ConnectionData[] => (
        mcpConnectors.map((connector): ConnectionData => {
            const connectorId = String(connector.connectorId);
            const toolCount = connector.tools?.length || 0;
            return {
                id: connectorId,
                connectionId: connectorId,
                workspaceIntegrationId: connector.integrationId == null ? undefined : String(connector.integrationId),
                integrationType: MCP_TOOL_NAME,
                name: connector.displayName,
                alias: connector.displayName,
                accountId: connector.serverUrl,
                status: mapMcpConnectorStatusToConnectionStatus(connector.status),
                statusLabel: connector.status,
                statusMessage: connector.reasonCode || undefined,
                connectedBy: '',
                connectedAt: 'Recent',
                permissionSummary: toolCount > 0
                    ? t('website.integrationsSettings.mcpToolCount', {
                        count: toolCount,
                        plural: toolCount === 1 ? '' : 's',
                    })
                    : '',
                permissions: {
                    read: toolCount > 0 ? t('website.integrationsSettings.mcpToolsSynced') : '',
                    write: '',
                },
            };
        })
    ), [mcpConnectors, t]);

    const getToolConnections = useCallback((tool: string): ConnectionData[] => {
        if (isMcpTool(tool)) {
            return getMcpConnectorConnections();
        }

        const providerKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool) || tool);
        const realIntegrationGroups = allIntegrations.filter(integration => normalizeIntegrationProvider(integration.provider) === providerKey);
        let connections = realIntegrationGroups.flatMap((integration) => {
            const profile = integration.profile_data || {};
            if (!integration.mappings?.length && !integration.connected) {
                return [];
            }
            const mappings = integration.mappings?.length ? integration.mappings : [undefined];
            const availability = tool === 'Klaviyo' ? resolveKlaviyoIntegrationAvailability(integration) : AVAILABLE_INTEGRATION_STATUS;

            return mappings.map((mapping, idx): ConnectionData => {
                const connId = String(mapping?.id || `${integration.provider}-${idx}`);
                const workspaceConnectionStatus: ConnectionData['status'] = integration.connected ? 'connected' : 'disconnected';
                const normalizedConnectionStatus = normalizeConnectionStatusValue(mapping?.status, workspaceConnectionStatus);
                const displayStatus = availability.isUnavailable
                    ? availability.status
                    : integration.connected && normalizedConnectionStatus === 'disconnected'
                        ? 'connected'
                        : normalizedConnectionStatus;
                const isShopifyConnection = tool === 'Shopify';
                const isGoogleAdsConnection = tool === 'Google Ads';
                const isBigCommerceConnection = tool === 'BigCommerce';
                const isFeishuConnection = tool === 'Feishu';
                const googleAdsAuthorizedAccountValue = isGoogleAdsConnection
                    ? resolveGoogleAdsAuthorizedAccountValue(profile, integration, mapping)
                    : '';
                const feishuAccountDisplayValue = isFeishuConnection
                    ? resolveFeishuConnectionDisplayName(profile, integration, mapping)
                    : '';
                const accountValue = isShopifyConnection
                    ? profile.shop_domain || profile.shopDomain || mapping?.account_name || mapping?.connection_id || ''
                    : isGoogleAdsConnection
                        ? googleAdsAuthorizedAccountValue
                        : isFeishuConnection
                            ? feishuAccountDisplayValue
                            : isBigCommerceConnection
                                ? mapping?.name || mapping?.account_name || mapping?.account_id || profile.accountName || profile.name || mapping?.connection_id || ''
                                : profile.accountId || mapping?.account_name || mapping?.account_id || profile.username || mapping?.connection_id || '';
                const connectionLabel = mapping?.label || '';
                const connectedByName = resolveConnectionUserDisplayName(mapping, profile);
                const googleAdsSubAccounts = isGoogleAdsConnection
                    ? (mapping?.sub_accounts || []).map((subAccount) => ({
                        accountName: String(subAccount.account_name || subAccount.customer_id || subAccount.account_id || 'Google Ads sub account'),
                        customerId: String(subAccount.customer_id || subAccount.account_id || ''),
                    })).filter(subAccount => subAccount.customerId)
                    : undefined;
                const connectionId = mapping?.connection_id || mapping?.nango_connection_id || undefined;
                const needsGoogleAdsAccountSelection = isGoogleAdsConnection
                    && Boolean(connectionId)
                    && (displayStatus === 'connected' || displayStatus === 'active')
                    && !googleAdsSubAccounts?.length;

                return {
                    id: connId,
                    connectionId,
                    workspaceIntegrationId: mapping?.id == null ? undefined : String(mapping.id),
                    slugId: mapping?.slugId || profile.slugId,
                    authType: String(mapping?.authType || integration.authType || ''),
                    native: Boolean(mapping?.native ?? integration.native),
                    integrationType: tool,
                    name: isGoogleAdsConnection
                        ? googleAdsAuthorizedAccountValue
                        : isFeishuConnection
                            ? feishuAccountDisplayValue
                            : profile.connection_name || profile.accountName || mapping?.name || mapping?.account_name || `${tool} Workspace`,
                    alias: connectionLabel,
                    accountId: String(accountValue),
                    status: displayStatus,
                    statusLabel: availability.label,
                    statusMessage: availability.message,
                    isUnavailable: availability.isUnavailable,
                    connectedBy: connectedByName,
                    connectedAt: mapping?.created_at || integration.created_at || 'Recent',
                    permissionSummary: '',
                    needsAccountSelection: needsGoogleAdsAccountSelection,
                    subAccounts: googleAdsSubAccounts,
                    permissions: {
                        read: '',
                        write: ''
                    }
                };
            });
        });

        connections = connections.map((connection) => {
            const overriddenAlias = connectionAliasOverrides[connection.id];

            if (typeof overriddenAlias === 'undefined') {
                return connection;
            }

            return {
                ...connection,
                alias: overriddenAlias,
            };
        });

        if (isSoloWorkspace && connections.length > 1) {
            return connections.slice(0, 1);
        }

        return connections;
    }, [allIntegrations, connectionAliasOverrides, getMcpConnectorConnections, isSoloWorkspace]);

    const openIntegrationDetail = (tool: string) => {
        const scrollContainer = getScrollContainer();

        if (scrollContainer) {
            listScrollTopRef.current = scrollContainer.scrollTop;
            pendingScrollTargetRef.current = 'detail';
        }

        setSelectedDetailTab(resolveDefaultIntegrationDetailTab(getToolConnections(tool).length > 0));
        setSelectedTool(tool);
    };

    const getToolCapabilityState = (tool: string, connection?: ConnectionData) => {
        const catalog = getDisplayCapabilityCatalog(tool, t);
        const availableReadCount = countCapabilityItems(catalog.read);
        const availableWriteCount = countCapabilityItems(catalog.write);
        const activeReadItemIds = getActiveCapabilityItemIds(tool, connection, 'read', catalog);
        const activeWriteItemIds = getActiveCapabilityItemIds(tool, connection, 'write', catalog);

        return {
            catalog,
            availableReadCount,
            availableWriteCount,
            activeReadItemIds,
            activeWriteItemIds,
            activeReadCount: activeReadItemIds.size,
            activeWriteCount: activeWriteItemIds.size,
            accessLabel: getCapabilityAccessLabel(t, activeReadItemIds.size, activeWriteItemIds.size),
        };
    };

    const getToolSortPriority = useCallback((tool: string) => {
        if (getToolConnections(tool).length > 0) {
            return 0;
        }

        if (isSupported(tool)) {
            return 1;
        }

        return 2;
    }, [getToolConnections]);

    const sortToolsByPriority = useCallback((tools: string[]) => (
        [...tools].sort((toolA, toolB) => {
            const priorityDelta = getToolSortPriority(toolA) - getToolSortPriority(toolB);

            if (priorityDelta !== 0) {
                return priorityDelta;
            }

            return (ALL_INTEGRATION_TOOL_ORDER.get(toolA) ?? Number.MAX_SAFE_INTEGER)
                - (ALL_INTEGRATION_TOOL_ORDER.get(toolB) ?? Number.MAX_SAFE_INTEGER);
        })
    ), [getToolSortPriority]);

    const connectedIntegrationTools = ALL_INTEGRATION_TOOLS
        .filter(tool => getToolConnections(tool).length > 0);
    const hasConnectedIntegrations = connectedIntegrationTools.length > 0;
    const visibleScenarioFilters = useMemo(
        () => SCENARIO_FILTERS.filter(category => category.id !== 'Connected' || hasConnectedIntegrations || isLoading),
        [hasConnectedIntegrations, isLoading]
    );

    const scenarioSections = SCENARIO_FILTERS
        .filter(cat => cat.id !== 'Connected')
        .map(cat => ({ category: cat.id, tools: [] as string[] }));

    const scenarioSectionMap = new Map(scenarioSections.map(section => [section.category, section]));

    ALL_INTEGRATIONS.forEach(group => {
        group.tools.forEach(tool => {
            const scenarioCategory = INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO[group.category];

            if (!scenarioCategory) {
                return;
            }

            const targetSection = scenarioSectionMap.get(scenarioCategory);

            if (targetSection && !targetSection.tools.includes(tool)) {
                targetSection.tools.push(tool);
            }
        });
    });

    scenarioSections.forEach(section => {
        section.tools = sortToolsByPriority(section.tools);
    });

    const hasSearchQuery = searchQuery.trim().length > 0;
    const selectedScenarioSection = scenarioSections.find(section => section.category === selectedCategory);
    const visibleConnectedTools = connectedIntegrationTools.filter(tool => matchesIntegrationSearch(tool, searchQuery));
    const visibleScenarioTools = (selectedScenarioSection?.tools || []).filter(tool => matchesIntegrationSearch(tool, searchQuery));
    const visibleSearchTools = hasSearchQuery
        ? sortToolsByPriority(ALL_INTEGRATION_TOOLS.filter(tool => matchesIntegrationSearch(tool, searchQuery)))
        : [];
    const selectedListTools = hasSearchQuery
        ? visibleSearchTools
        : selectedCategory === 'Connected'
            ? visibleConnectedTools
            : visibleScenarioTools;
    const selectedToolConnections = selectedTool ? getToolConnections(selectedTool) : [];
    const lastStableConnections = selectedTool ? (lastStableConnectionsByToolRef.current[selectedTool] || []) : [];
    const isDisconnectingSelectedTool = selectedTool ? disconnectingTools.has(selectedTool) : false;
    const selectedToolDisplayConnections = selectedTool && isDisconnectingSelectedTool
        && lastStableConnections.length > selectedToolConnections.length
        ? lastStableConnections
        : selectedToolConnections;
    const selectedToolPrimaryConnection = selectedToolDisplayConnections[0];
    const selectedToolCapabilityState = selectedTool
        ? getToolCapabilityState(selectedTool, selectedToolPrimaryConnection)
        : null;
    useEffect(() => {
        if (!selectedTool || selectedToolConnections.length === 0 || syncingTools.has(selectedTool)) {
            return;
        }

        lastStableConnectionsByToolRef.current[selectedTool] = selectedToolConnections;
    }, [selectedTool, selectedToolConnections, syncingTools]);

    useEffect(() => {
        if (!pendingScrollTargetRef.current || typeof window === 'undefined') {
            return undefined;
        }

        const scrollContainer = getScrollContainer();
        if (!scrollContainer) {
            pendingScrollTargetRef.current = null;
            return undefined;
        }

        const nextScrollTarget = pendingScrollTargetRef.current;
        pendingScrollTargetRef.current = null;
        const targetTop = nextScrollTarget === 'detail' ? 0 : listScrollTopRef.current;
        const frameId = window.requestAnimationFrame(() => {
            scrollContainer.scrollTo({ top: targetTop });
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [getScrollContainer, selectedTool]);

    useEffect(() => {
        if (!selectedTool) {
            return;
        }

        setSelectedDetailTab(resolveDefaultIntegrationDetailTab(selectedToolDisplayConnections.length > 0));
    }, [selectedTool, selectedToolDisplayConnections.length]);

    useEffect(() => {
        setIsAddConnectionUpgradeModalOpen(false);
    }, [selectedTool]);

    useEffect(() => {
        if (selectedCategory === 'Connected' && isLoading) {
            return;
        }

        if (visibleScenarioFilters.some(category => category.id === selectedCategory)) {
            return;
        }

        setSelectedCategory(visibleScenarioFilters[0]?.id ?? 'Store Ops');
    }, [isLoading, selectedCategory, visibleScenarioFilters]);

    const renderPermissionSection = () => {
        if (!selectedTool || !selectedToolCapabilityState) {
            return null;
        }

        const catalog = selectedToolCapabilityState.catalog;
        const readItems = catalog.read
            .flatMap(section => section.items)
            .map(item => item.label)
            .filter(Boolean);
        const writeItems = catalog.write
            .flatMap(section => section.items)
            .map(item => item.label)
            .filter(Boolean);

        return (
            <div className="space-y-8">
                {/* How it works Module */}
                <div className="space-y-3">
                    <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.howItWorks')}</h3>
                    <p className="text-sm font-normal leading-relaxed text-[#666F8D]">
                        {getToolDescription(t, selectedTool)}
                    </p>
                </div>

                {/* What can Bustly view? Module */}
                {selectedToolCapabilityState.availableReadCount > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.whatBustlyCanView')}</h3>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm font-normal text-[#666F8D]">
                            {readItems.map((item, idx) => (
                                <li key={`read-item-${idx}`}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* What can Bustly help do? Module */}
                {selectedToolCapabilityState.availableWriteCount > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.whatBustlyCanDo')}</h3>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm font-normal text-[#666F8D]">
                            {writeItems.map((item, idx) => (
                                <li key={`write-item-${idx}`}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const renderIntegrationCard = (tool: string) => {
        const connections = getToolConnections(tool);
        const connectionCount = connections.filter(isIntegrationConnectionActive).length;
        const isToolSupported = isSupported(tool.toLowerCase().replace(/\s+/g, '_'));
        const isConnected = connectionCount > 0;

        // Get capabilities for preview
        const catalog = getCapabilityCatalog(tool, connections[0], integrationPermissionCatalog, t);
        const readCapabilities = catalog.read.flatMap(section => section.items).map(item => item.id).filter(Boolean);
        const writeCapabilities = catalog.write.flatMap(section => section.items).map(item => item.id).filter(Boolean);
        const hasCapabilityPreview = readCapabilities.length > 0 || writeCapabilities.length > 0;
        const Container = isToolSupported ? 'button' : 'div';
        const isCustomMcpTool = isMcpTool(tool);

        return (
            <Container
                key={tool}
                {...(isToolSupported ? {
                    type: 'button',
                    onClick: () => openIntegrationDetail(tool),
                } : {})}
                className={clsx(
                    "flex flex-col w-full rounded-2xl border bg-white text-left transition-all relative group",
                    isToolSupported
                        ? "cursor-pointer border-[#E8EBF3] hover:border-[#1A162F] hover:shadow-[0_10px_24px_rgba(26,22,47,0.08)]"
                        : "border-[#E8EBF3]"
                )}
            >
                <div className="flex w-full items-center justify-between gap-4 px-5 py-3">
                    <div className="flex items-center gap-3">
                        <div
                            className={clsx(
                                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                                isCustomMcpTool ? 'bg-[#F4F5F8] text-[#1A162F]' : getBrandIconFrameClassName(tool)
                            )}
                        >
                            {isCustomMcpTool ? (
                                <Database size={20} weight="regular" />
                            ) : (
                                <img
                                    src={getBrandIcon(tool)}
                                    alt={tool}
                                    className={getBrandIconImageClassName(tool, 'h-5 w-5')}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://cdn.brandfetch.io/google.com/icon';
                                    }}
                                />
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium leading-none text-[#1A162F]">{tool}</h3>
                            {isConnected ? (
                                <span className="inline-flex items-center rounded-full border border-[#CFE7D8] bg-[#EEF9F2] px-2 py-0.5 text-[10px] font-semibold leading-none text-[#1D7A45]">
                                    {t('website.integrationsSettings.connected')}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {/* Action Icon */}
                    {isToolSupported ? (
                        <div className="shrink-0">
                             <CaretRight size={16} weight="regular" className="text-[#A0A8C0] group-hover:text-[#1A162F]" />
                        </div>
                    ) : (
                        <span className="inline-flex shrink-0 rounded-full bg-[#F4F5F8] px-2.5 py-1 text-[11px] font-medium text-[#8A93B2]">
                            {t('website.integrationsSettings.soon')}
                        </span>
                    )}
                </div>

                {/* Bottom Section: How it works Preview & Connected Accounts */}
                {isToolSupported && (
                    <div className="bg-white px-5 pb-4 pt-1 rounded-b-2xl flex flex-col gap-2.5">
                        <div className="flex flex-col gap-1">
                            <span className="text-[11px] font-medium text-[#8A93B2]">{t('website.integrationsSettings.howItWorks')}</span>
                            <p
                                className="overflow-hidden text-[13px] font-normal leading-5 text-[#666F8D]"
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {getToolDescription(t, tool)}
                            </p>
                        </div>
                        {connectionCount > 0 && (
                            <div className="pt-0.5 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-medium text-[#8A93B2]">{t('website.integrationsSettings.connectionsTab')}</span>
                                    <span className="inline-flex h-5 items-center justify-center rounded-full bg-[#F4F5F8] px-2 text-[10px] font-semibold text-[#666F8D]">
                                        <span className="leading-none">{connectionCount}</span>
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2.5 w-full">
                                    {connections.slice(0, 3).map(conn => (
                                        <div
                                            key={conn.id}
                                            className="flex items-center justify-between gap-3 rounded-lg border border-[#E8EBF3] bg-white px-3 py-2"
                                        >
                                            <div className="flex flex-col min-w-0">
                                                <span className="truncate text-xs font-medium text-[#1A162F]" title={conn.accountId}>
                                                    {conn.accountId}
                                                </span>
                                                {conn.alias && (
                                                    <span className="text-[10px] text-[#8A93B2] truncate mt-px" title={conn.alias}>
                                                        {conn.alias}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Container>
        );
    };

    const renderMcpIntegrationDetail = () => {
        const activeMcpConnectors = mcpConnectors.filter(connector => mapMcpConnectorStatusToConnectionStatus(connector.status) === 'connected');

        return (
            <div className="space-y-6">
                <section className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 items-center gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F4F5F8] text-[#1A162F]">
                                <Database size={24} weight="regular" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="text-xl font-bold text-text-main">{MCP_TOOL_NAME}</h2>
                                    {activeMcpConnectors.length > 0 ? (
                                        <span className="inline-flex items-center rounded-full border border-[#CFE7D8] bg-[#EEF9F2] px-2 py-0.5 text-[10px] font-semibold leading-none text-[#1D7A45]">
                                            {t('website.integrationsSettings.connected')}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => openMcpConnectorModal('url')}
                            disabled={!canConnect || isSavingMcpConnector || mcpFlow.state.kind === 'creating-draft'}
                            title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                            className="flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {mcpFlow.state.kind === 'creating-draft' ? (
                                <CircleNotch size={16} weight="regular" className="animate-spin" />
                            ) : (
                                <Plus size={16} weight="regular" />
                            )}
                            {activeMcpConnectors.length > 0
                                ? t('website.integrationsSettings.addConnection')
                                : t('website.integrationsSettings.connect')}
                        </button>
                    </div>
                </section>

                <section className="space-y-5 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                    <div className="space-y-3">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.howItWorks')}</h3>
                        <p className="text-sm font-normal leading-relaxed text-[#666F8D]">
                            {getToolDescription(t, MCP_TOOL_NAME)}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => openMcpConnectorModal('url')}
                        disabled={!canConnect || isSavingMcpConnector || mcpFlow.state.kind === 'creating-draft'}
                        title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                        className="flex min-h-[112px] w-full flex-col items-start gap-3 rounded-xl border border-[#E8EBF3] bg-white p-4 text-left transition-colors hover:border-[#1A162F] hover:bg-[#FCFCFC] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F4F5F8] text-[#1A162F]">
                            <Plus size={18} weight="regular" />
                        </span>
                        <span className="text-sm font-semibold text-[#1A162F]">{t('website.integrationsSettings.mcpUrlMethod')}</span>
                        <span className="text-xs leading-5 text-[#666F8D]">{t('website.integrationsSettings.mcpOAuthDescription')}</span>
                    </button>
                </section>

                <section className="space-y-4 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                    <div className="flex items-center justify-between gap-4">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.connectionsTab')}</h3>
                        {isLoadingMcpConnectors ? (
                            <span className="inline-flex items-center gap-2 text-xs font-medium text-[#666F8D]">
                                <CircleNotch size={14} className="animate-spin" />
                                {t('website.integrationsSettings.loadingConnectionAccess')}
                            </span>
                        ) : null}
                    </div>

                    {mcpConnectorError && !mcpModalMode ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm leading-5 text-red-700">
                            {mcpConnectorError}
                        </div>
                    ) : null}

                    {mcpConnectors.length > 0 ? (
                        <div className="space-y-3">
                            {mcpConnectors.map((connector) => {
                                const status = mapMcpConnectorStatusToConnectionStatus(connector.status);
                                const tools = connector.tools || [];
                                const toolCount = tools.length;
                                const isDisconnectingMcpConnector = disconnectingMcpConnectorIds.has(connector.connectorId);
                                const isTestingMcpConnector = testingMcpConnectorIds.has(connector.connectorId);
                                const isSyncingMcpConnector = syncingMcpConnectorIds.has(connector.connectorId);
                                return (
                                    <div
                                        key={connector.connectorId}
                                        className="space-y-4 rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-4"
                                    >
                                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                            <div className="min-w-0 space-y-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-sm font-semibold text-[#1A162F]">{connector.displayName}</span>
                                                    <span className={clsx(
                                                        "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none",
                                                        status === 'connected'
                                                            ? "bg-[#EEF9F2] text-[#1D7A45] ring-1 ring-[#CFE7D8]"
                                                            : status === 'error'
                                                                ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                                                                : "bg-[#F4F5F8] text-[#666F8D] ring-1 ring-[#E8EBF3]"
                                                    )}>
                                                        {connector.status}
                                                    </span>
                                                </div>
                                                <p className="truncate text-xs text-[#666F8D]" title={connector.serverUrl}>
                                                    {connector.serverUrl}
                                                </p>
                                            </div>
                                            <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs font-medium text-[#666F8D]">
                                                <span>{t('website.integrationsSettings.mcpToolCount', {
                                                    count: toolCount,
                                                    plural: toolCount === 1 ? '' : 's',
                                                })}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => void handleMcpConnectorTest(connector)}
                                                    disabled={!canConnect || isTestingMcpConnector}
                                                    title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isTestingMcpConnector ? <CircleNotch size={14} className="animate-spin" /> : null}
                                                    {t('website.integrationsSettings.mcpTest')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void handleMcpConnectorSync(connector)}
                                                    disabled={!canConnect || isSyncingMcpConnector}
                                                    title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isSyncingMcpConnector ? <CircleNotch size={14} className="animate-spin" /> : null}
                                                    {t('website.integrationsSettings.mcpSync')}
                                                </button>
                                                {tools.length > 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setMcpToolManagerConnectorId(previous => (
                                                            previous === connector.connectorId ? null : connector.connectorId
                                                        ))}
                                                        disabled={!canConnect}
                                                        title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <SlidersHorizontal size={14} weight="regular" />
                                                        {mcpToolManagerConnectorId === connector.connectorId
                                                            ? t('website.integrationsSettings.mcpHideTools')
                                                            : t('website.integrationsSettings.mcpManageTools')}
                                                    </button>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        void handleMcpConnectorDisconnect(connector);
                                                    }}
                                                    disabled={!canDisconnect || isDisconnectingMcpConnector}
                                                    title={!canDisconnect ? t('website.integrationsSettings.disconnectPermissionDenied') : undefined}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-100 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                        {isDisconnectingMcpConnector ? (
                                                            <CircleNotch size={14} weight="regular" className="animate-spin" />
                                                        ) : (
                                                            <Trash size={14} weight="regular" />
                                                        )}
                                                        {t('website.integrationsSettings.disconnect')}
                                                </button>
                                            </div>
                                        </div>
                                        {mcpToolManagerConnectorId === connector.connectorId && tools.length > 0 ? (
                                            <div className="overflow-x-auto rounded-lg border border-[#E8EBF3] bg-white">
                                                <table className="min-w-full divide-y divide-[#E8EBF3] text-left text-xs">
                                                    <thead className="bg-[#F8F9FC] text-[#666F8D]">
                                                        <tr>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolName')}</th>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolEnabled')}</th>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolAccess')}</th>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolRisk')}</th>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolRequireConfirmation')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-[#E8EBF3]">
                                                        {tools.map((tool) => {
                                                            const toolId = tool.toolId ?? tool.id ?? tool.name;
                                                            const updateKey = `${connector.connectorId}:${toolId}`;
                                                            const isUpdatingTool = updatingMcpToolKeys.has(updateKey);
                                                            return (
                                                                <tr key={String(toolId)} className="align-middle">
                                                                    <td className="max-w-[280px] px-3 py-2">
                                                                        <div className="truncate font-medium text-[#1A162F]" title={tool.name}>
                                                                            {tool.name}
                                                                        </div>
                                                                        {tool.description ? (
                                                                            <div className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-[#666F8D]" title={tool.description}>
                                                                                {tool.description}
                                                                            </div>
                                                                        ) : null}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={Boolean(tool.enabled)}
                                                                            disabled={!canConnect || isUpdatingTool}
                                                                            onChange={(event) => handleMcpToolUpdate(connector, tool, { enabled: event.target.checked })}
                                                                            className="h-4 w-4 rounded border-gray-300 text-[#1A162F] focus:ring-[#1A162F]"
                                                                            aria-label={t('website.integrationsSettings.mcpToolEnabled')}
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <select
                                                                            value={tool.accessLevelRequired || 'WRITE'}
                                                                            disabled={!canConnect || isUpdatingTool}
                                                                            onChange={(event) => handleMcpToolUpdate(connector, tool, {
                                                                                accessLevelRequired: event.target.value as NonNullable<McpConnectorToolUpdateRequest['accessLevelRequired']>,
                                                                            })}
                                                                            className="h-8 rounded-lg border border-[#D9DDE8] bg-white px-2 text-xs font-medium text-[#1A162F] outline-none focus:border-[#1A162F]"
                                                                        >
                                                                            {mcpToolAccessOptions.map(option => (
                                                                                <option key={option} value={option}>{option}</option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <select
                                                                            value={tool.riskLevel || 'MEDIUM'}
                                                                            disabled={!canConnect || isUpdatingTool}
                                                                            onChange={(event) => handleMcpToolUpdate(connector, tool, {
                                                                                riskLevel: event.target.value as NonNullable<McpConnectorToolUpdateRequest['riskLevel']>,
                                                                            })}
                                                                            className="h-8 rounded-lg border border-[#D9DDE8] bg-white px-2 text-xs font-medium text-[#1A162F] outline-none focus:border-[#1A162F]"
                                                                        >
                                                                            {mcpToolRiskOptions.map(option => (
                                                                                <option key={option} value={option}>{option}</option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={Boolean(tool.requireConfirmation)}
                                                                            disabled={!canConnect || isUpdatingTool}
                                                                            onChange={(event) => handleMcpToolUpdate(connector, tool, { requireConfirmation: event.target.checked })}
                                                                            className="h-4 w-4 rounded border-gray-300 text-[#1A162F] focus:ring-[#1A162F]"
                                                                            aria-label={t('website.integrationsSettings.mcpToolRequireConfirmation')}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-[#E8EBF3] bg-[#FCFCFC] px-4 py-8 text-center text-sm text-[#666F8D]">
                            {t('website.integrationsSettings.mcpNoConnectors')}
                        </div>
                    )}
                </section>

                {mcpConnectors.length > 0 ? (
                    <section className="space-y-4 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.mcpMemberAccess')}</h3>
                        <ConnectionAccessSettings
                            key={`${MCP_TOOL_NAME}-mcp-connection-access`}
                            showHeader={false}
                            providerFilter={MCP_TOOL_NAME}
                        />
                    </section>
                ) : null}
            </div>
        );
    };

    const renderIntegrationDetail = () => {
        if (!selectedTool || !selectedToolCapabilityState) {
            return null;
        }

        if (isMcpTool(selectedTool)) {
            return renderMcpIntegrationDetail();
        }

        const normalizedTool = selectedTool.toLowerCase().replace(/\s+/g, '_');
        const isToolSupported = isSupported(normalizedTool);
        const hasConnections = selectedToolDisplayConnections.length > 0;
        const unavailableKlaviyoConnection = selectedTool === 'Klaviyo'
            ? selectedToolDisplayConnections.find(connection => connection.isUnavailable)
            : null;
        const isSyncing = syncingTools.has(selectedTool);
        const isDisconnectingSelectedTool = disconnectingTools.has(selectedTool);
        const startedConnectionCount = connectStartConnectionCountsRef.current.get(selectedTool);
        const hasConnectionCreatedDuringSync = startedConnectionCount !== undefined
            && selectedToolDisplayConnections.length > startedConnectionCount;
        const canAddConnections = canConnect && !isSoloWorkspace;
        const showFirstConnectionAction = isToolSupported && !hasConnections;
        const showPrimaryAction = showFirstConnectionAction || (canConnect && canAddConnections);
        const showSoloUpgradeAddConnection = canConnect && isSoloWorkspace && hasConnections && isToolSupported;
        const showApiKeyFallbackAction = API_KEY_FALLBACK_TOOLS.has(selectedTool)
            && isToolSupported
            && canConnect
            && (!hasConnections || canAddConnections);
        const isPrimaryActionBusy = isSyncing && !isDisconnectingSelectedTool && !hasConnectionCreatedDuringSync;
        const primaryActionLabel = isPrimaryActionBusy
            ? t('website.integrationsSettings.connecting')
            : hasConnections
                ? t('website.integrationsSettings.addConnection')
                : isToolSupported
                    ? t('website.integrationsSettings.connect')
                    : t('website.integrationsSettings.soon');
        const handlePrimaryActionClick = () => {
            if (showSoloUpgradeAddConnection) {
                handleOpenAddConnectionUpgrade();
                return;
            }

            void handleConnect(selectedTool);
        };

        return (
            <div className="space-y-6">
                <section className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 items-center gap-4">
                            <div
                                className={clsx(
                                    'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                                    getBrandIconFrameClassName(selectedTool)
                                )}
                            >
                                <img
                                    src={getBrandIcon(selectedTool)}
                                    alt={selectedTool}
                                    className={getBrandIconImageClassName(selectedTool, 'h-6 w-6')}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://cdn.brandfetch.io/google.com/icon';
                                    }}
                                />
                            </div>

                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="text-xl font-bold text-text-main">{selectedTool}</h2>
                                    {unavailableKlaviyoConnection ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium tracking-[0.02em] text-amber-800 ring-1 ring-amber-500/25">
                                            {t('website.integrationsSettings.attentionNeeded')}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-4">
                            {!hasConnections && isToolSupported && canConnect ? (
                                <button
                                    onClick={() => handleRegister(selectedTool)}
                                    className="text-xs font-medium text-[#667085] transition-colors hover:text-[#1A162F] hover:underline"
                                >
                                    {t('website.integrationsSettings.dontHaveAccountSignUp')}
                                </button>
                            ) : null}
                            {showPrimaryAction || showSoloUpgradeAddConnection ? (
                                <button
                                    onClick={handlePrimaryActionClick}
                                    disabled={!isToolSupported || isPrimaryActionBusy}
                                    className={clsx(
                                        "flex items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 text-sm font-medium shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors",
                                        isToolSupported && !showSoloUpgradeAddConnection
                                            ? "bg-[#1A162F] text-white hover:bg-primary/90 disabled:cursor-wait disabled:opacity-70"
                                            : showSoloUpgradeAddConnection
                                                ? "bg-[#1A162F] text-white hover:bg-primary/90"
                                                : "cursor-not-allowed bg-[#F4F5F8] text-[#8A93B2]"
                                    )}
                                >
                                    {isPrimaryActionBusy ? (
                                        <CircleNotch size={16} weight="regular" className="animate-spin" />
                                    ) : hasConnections ? (
                                        <Plus size={16} weight="regular" />
                                    ) : null}
                                    {primaryActionLabel}
                                </button>
                            ) : null}
                            {showApiKeyFallbackAction ? (
                                <button
                                    type="button"
                                    onClick={() => handleApiKeyFallbackConnection(selectedTool)}
                                    disabled={isPrimaryActionBusy}
                                    className="text-xs font-medium text-[#667085] transition-colors hover:text-[#1A162F] hover:underline disabled:cursor-wait disabled:opacity-60"
                                >
                                    {t('website.integrationsSettings.connectWithApiKey')}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </section>

                {unavailableKlaviyoConnection ? (
                    <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                        <h3 className="text-sm font-semibold text-amber-900">{t('website.integrationsSettings.klaviyoUnavailable')}</h3>
                        <p className="mt-2 text-sm leading-6 text-amber-800">
                            {unavailableKlaviyoConnection.statusMessage || t('website.integrationsSettings.klaviyoUnavailableDescription')}
                        </p>
                    </section>
                ) : null}

                <section className="space-y-5 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                    <div className="flex min-h-[42px] items-end justify-between gap-4 border-b border-[#ECEFF5]">
                        <div className="flex h-full items-end gap-6">
                            <button
                                onClick={() => setSelectedDetailTab('permissions')}
                                className={clsx(
                                    "relative pb-3 text-sm font-medium transition-colors",
                                    selectedDetailTab === 'permissions'
                                        ? "text-[#1A162F]"
                                        : "text-[#666F8D] hover:text-[#1A162F]"
                                )}
                            >
                                {t('website.integrationsSettings.descriptionTab')}
                                {selectedDetailTab === 'permissions' ? (
                                    <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#1A162F]" />
                                ) : null}
                            </button>
                            {hasConnections ? (
                                <button
                                    onClick={() => setSelectedDetailTab('connections')}
                                    className={clsx(
                                        "relative flex items-center gap-1.5 pb-3 text-sm font-medium transition-colors",
                                        selectedDetailTab === 'connections'
                                            ? "text-[#1A162F]"
                                            : "text-[#666F8D] hover:text-[#1A162F]"
                                    )}
                                >
                                    <span>{t('website.integrationsSettings.connectionsTab')}</span>
                                    <span className={clsx(
                                        "inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium leading-none transition-colors",
                                        selectedDetailTab === 'connections'
                                            ? "bg-[#1A162F]/10 text-[#1A162F]"
                                            : "bg-[#F4F5F8] text-[#8A93B2]"
                                    )}>
                                        {selectedToolConnections.length}
                                    </span>
                                    {selectedDetailTab === 'connections' ? (
                                        <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#1A162F]" />
                                    ) : null}
                                </button>
                            ) : null}
                        </div>

                    </div>

                    <div className="min-h-[420px]">
                        {selectedDetailTab === 'connections' && hasConnections ? (
                            <div className="space-y-4">
                                <ConnectionAccessSettings
                                    key={`${selectedTool}-detail-connections`}
                                    showHeader={false}
                                    providerFilter={selectedTool}
                                />
                            </div>
                        ) : (
                            renderPermissionSection()
                        )}
                    </div>
                </section>
            </div>
        );
    };

    // 骨架屏组件
    const renderSkeleton = () => (
        <div className="flex flex-col gap-4">
            {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="flex flex-col w-full rounded-2xl border border-gray-200 bg-white text-left overflow-hidden">
                    <div className="flex w-full items-center justify-between gap-4 p-5">
                        <div className="flex items-center gap-3 w-full">
                            <div className="shrink-0 rounded-xl overflow-hidden">
                                <Skeleton width="48px" height="48px" />
                            </div>
                            <div className="flex items-center gap-2 flex-1">
                                <Skeleton width="100px" height="18px" />
                                <div className="rounded-full overflow-hidden">
                                    <Skeleton width="80px" height="20px" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50/50 border-t border-gray-100 p-5">
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-2.5">
                                <Skeleton width="120px" height="14px" />
                                <div className="space-y-1.5">
                                    <Skeleton width="100%" height="14px" />
                                    <Skeleton width="60%" height="14px" />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2.5">
                                <Skeleton width="130px" height="14px" />
                                <div className="space-y-1.5">
                                    <Skeleton width="90%" height="14px" />
                                    <Skeleton width="40%" height="14px" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const shouldShowIntegrationsLoadingSkeleton = selectedCategory === 'Connected'
        && isLoading
        && allIntegrations.length === 0
        && !hasSearchQuery
        && !selectedTool;

    return (
        <div ref={rootRef} className="space-y-6">
            {/* Unified Compact Header */}
            <div className="flex flex-col gap-5 pb-2">
                {selectedTool ? (
                    <button
                        type="button"
                        onClick={closeIntegrationDetail}
                        className="inline-flex items-center gap-3 self-start text-left text-[#1A162F] transition-colors hover:text-[#1A162F]/80"
                    >
                        <CaretLeft size={24} weight="regular" />
                        <span className="text-[20px] font-medium leading-none tracking-[-0.01em]">{selectedTool}</span>
                    </button>
                ) : (
                    <>
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">{t('website.integrationsSettings.integrations')}</h2>
                            </div>
                            {mode === 'admin' && (
                                <div className="relative w-64">
                                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]" size={16} weight="regular" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder={t('website.integrationsSettings.searchIntegrations')}
                                        className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] focus:outline-none transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    {hasSearchQuery ? (
                                        <button
                                            type="button"
                                            onClick={clearSearch}
                                            aria-label={t('website.integrationsSettings.clearSearch')}
                                            title={t('website.integrationsSettings.clearSearch')}
                                            className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                                        >
                                            <XCircle size={16} weight="fill" />
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </div>

                    </>
                )}
            </div>

            {selectedTool ? (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {renderIntegrationDetail()}
                </div>
            ) : (
                <div className="animate-in flex flex-col gap-5 fade-in slide-in-from-bottom-2 duration-300 md:flex-row md:items-start">
                    <aside
                        aria-label={t('website.integrationsSettings.integrations')}
                        className="w-full shrink-0 md:w-[224px] md:border-r md:border-[#E8EBF3] md:pr-4 md:sticky md:top-6"
                    >
                        <nav
                            aria-label={t('website.integrationsSettings.integrations')}
                            className="flex gap-1 overflow-x-auto pb-1 md:flex-col md:overflow-visible"
                        >
                            {visibleScenarioFilters.map(category => {
                                const isActive = !hasSearchQuery && selectedCategory === category.id;
                                const categoryToolCount = category.id === 'Connected'
                                    ? connectedIntegrationTools.length
                                    : scenarioSections.find(section => section.category === category.id)?.tools.length || 0;

                                return (
                                    <button
                                        key={category.id}
                                        type="button"
                                        aria-current={isActive ? 'page' : undefined}
                                        onClick={() => setSelectedCategory(category.id)}
                                        className={clsx(
                                            "flex min-h-10 shrink-0 items-center gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal transition-colors md:w-full",
                                            isActive
                                                ? "bg-[#E6E7EA] text-[#1A162F]"
                                                : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                                        )}
                                    >
                                        <span className="min-w-0 flex-1 truncate">
                                            {localizedScenarioLabels[category.id]}
                                        </span>
                                        <span className={clsx(
                                            "text-xs font-normal tabular-nums flex items-center justify-center min-w-4 min-h-4",
                                            isActive ? "text-[#666F8D]" : "text-[#8A93B2]"
                                        )}>
                                            {category.id === 'Connected' && isLoading ? (
                                                <CircleNotch size={12} className="animate-spin text-current" />
                                            ) : (
                                                categoryToolCount
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>
                    </aside>

                    <section id="integrations-list" className="min-w-0 flex-1">
                        {hasSearchQuery ? (
                            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
                                <h3 className="text-sm font-medium text-[#1A162F]">
                                    {t('website.integrationsSettings.searchResults')}
                                </h3>
                                <span className="text-sm font-normal text-[#666F8D]">
                                    {t('website.integrationsSettings.searchResultsSummary', {
                                        count: visibleSearchTools.length,
                                        plural: visibleSearchTools.length === 1 ? '' : 'es',
                                    })}
                                </span>
                            </div>
                        ) : null}

                        {shouldShowIntegrationsLoadingSkeleton ? (
                            renderSkeleton()
                        ) : selectedListTools.length > 0 ? (
                            <div className="flex flex-col gap-4">
                                {selectedListTools.map(tool => renderIntegrationCard(tool))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#E8EBF3] bg-[#FCFCFC] py-20">
                                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F4F5F8] text-[#A0A8C0]">
                                    <SquaresFour size={24} weight="regular" />
                                </div>
                                <p className="text-sm font-normal text-[#666F8D]">
                                    {searchQuery
                                        ? (selectedCategory === 'Connected'
                                            ? t('website.integrationsSettings.noConnectedIntegrationsFound', { query: searchQuery })
                                            : t('website.integrationsSettings.noIntegrationsFound', { query: searchQuery }))
                                        : t('website.integrationsSettings.noIntegrationsInCategory', { category: localizedScenarioLabels[selectedCategory] })}
                                </p>
                            </div>
                        )}
                    </section>
                </div>
            )}

            <GoogleAdsAccountSelectionModal
                isOpen={Boolean(googleAdsAccountSelectionConnectionId)}
                connectionId={googleAdsAccountSelectionConnectionId}
                onClose={closeGoogleAdsAccountSelection}
                onSaved={handleGoogleAdsAccountSelectionSaved}
            />

            <Modal
                isOpen={!!editingLabelConnection}
                onClose={() => {
                    if (!isSavingConnectionLabel) {
                        closeEditConnectionLabelModal();
                    }
                }}
                title={t('website.integrationsSettings.editLabel')}
                width="max-w-md"
                bodyClassName="px-6 pb-6 pt-0"
            >
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-text-main mb-1.5">{t('website.integrationsSettings.label')}</label>
                        <input
                            value={editingLabelValue}
                            onChange={(e) => setEditingLabelValue(e.target.value)}
                            placeholder={t('website.integrationsSettings.labelPlaceholder')}
                            disabled={isSavingConnectionLabel}
                            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all"
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={closeEditConnectionLabelModal}
                            disabled={isSavingConnectionLabel}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-[#666F8D] transition-colors hover:bg-gray-100 hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleSaveConnectionLabel}
                            disabled={isSavingConnectionLabel}
                            className="rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isSavingConnectionLabel ? (
                                <span className="inline-flex items-center gap-2">
                                    <CircleNotch size={14} className="animate-spin" />
                                    {t('website.integrationsSettings.saving')}
                                </span>
                            ) : t('common.save')}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isAddConnectionUpgradeModalOpen}
                onClose={() => setIsAddConnectionUpgradeModalOpen(false)}
                title={t('website.integrationsSettings.upgradeAddConnections')}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    <p className="text-sm leading-6 text-text-sub">
                        {t('website.integrationsSettings.upgradeAddConnectionsDescription', {
                            tool: selectedTool || t('website.integrationsSettings.thisIntegration'),
                            plan: t('website.integrationsSettings.teamPlan'),
                        })}
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setIsAddConnectionUpgradeModalOpen(false)}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                        >
                            {t('website.integrationsSettings.notNow')}
                        </button>
                        <button
                            onClick={handleOpenAddConnectionUpgradePricing}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                        >
                            {t('website.integrationsSettings.upgradeToTeam')}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!blockedToolName}
                onClose={() => setBlockedToolName(null)}
                title={t('website.integrationsSettings.connectRestrictedToolTitle', { tool: blockedToolName })}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    <p className="text-sm leading-6 text-text-sub">
                        {t('website.integrationsSettings.connectRestrictedToolDescription', { tool: blockedToolName })}
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setBlockedToolName(null)}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                        >
                            {t('website.integrationsSettings.notNow')}
                        </button>
                        <button
                            onClick={() => {
                                setBlockedToolName(null);
                                modal.openModal('pricing');
                            }}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                        >
                            {t('website.integrationsSettings.upgradeToTeam')}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!storeLimitPrompt}
                onClose={() => setStoreLimitPrompt(null)}
                title={storeLimitPrompt?.title || t('website.integrationsSettings.storeConnectionLimitReached')}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    <p className="text-sm leading-6 text-text-sub">
                        {storeLimitPrompt?.description}
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setStoreLimitPrompt(null)}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                        >
                            {storeLimitPrompt?.secondaryActionLabel || t('website.integrationsSettings.notNow')}
                        </button>
                        <button
                            onClick={handleStoreLimitPrimaryAction}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                        >
                            {storeLimitPrompt?.primaryActionLabel || t('website.integrationsSettings.upgrade')}
                        </button>
                    </div>
                </div>
            </Modal>

            <ContactSalesModal
                isOpen={isStoreLimitContactSalesOpen}
                onClose={() => setIsStoreLimitContactSalesOpen(false)}
            />

            <IntegrationApiKeyModal
                isOpen={!!apiKeyTool}
                onClose={() => {
                    if (!apiKeyTool || !syncingTools.has(apiKeyTool)) {
                        resetConnectionForm();
                    }
                }}
                toolName={apiKeyTool || ''}
                apiKey={apiKeyValue}
                onApiKeyChange={setApiKeyValue}
                accountLabel={apiKeyAccountLabel}
                onAccountLabelChange={setApiKeyAccountLabel}
                storeUrl={prestaShopStoreUrl}
                onStoreUrlChange={setPrestaShopStoreUrl}
                onConfirm={handleApiKeyConnection}
                isLoading={apiKeyTool ? syncingTools.has(apiKeyTool) : false}
            />

            <IntegrationUrlModal
                isOpen={!!connectingTool && Boolean(URL_INPUT_CONFIG[connectingTool])}
                onClose={resetConnectionForm}
                toolName={connectingTool || ''}
                urlConfig={connectingTool && URL_INPUT_CONFIG[connectingTool] ? URL_INPUT_CONFIG[connectingTool] : undefined}
                storeUrl={storeUrl}
                onStoreUrlChange={(value) => {
                    setStoreUrl(value);
                    setUrlConnectionError(null);
                }}
                connectionName={connectionName}
                onConnectionNameChange={setConnectionName}
                connectionAlias={connectionAlias}
                onConnectionAliasChange={setConnectionAlias}
                onConfirm={handleUrlConnection}
                isLoading={connectingTool ? syncingTools.has(connectingTool) : false}
                errorText={urlConnectionError}
                extraFields={connectingTool === 'BigCommerce' ? [
                    {
                        key: 'store_hash',
                        label: t('website.integrationsSettings.storeHash'),
                        placeholder: 'abc123xyz',
                        value: bigCommerceStoreHash,
                        onChange: setBigCommerceStoreHash
                    },
                    {
                        key: 'account_uuid',
                        label: t('website.integrationsSettings.accountUuid'),
                        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                        value: bigCommerceAccountUuid,
                        onChange: setBigCommerceAccountUuid
                    }
                ] : []}
            />
            {isMcpUiEnabled() ? (
                <McpConnectorModal
                    isOpen={Boolean(mcpModalMode)}
                    mode={mcpModalMode}
                    showManualSetup={mcpFlow.state.kind === 'manual-setup'}
                    onClose={closeMcpConnectorModal}
                    onStartOAuth={handleMcpOAuthStart}
                    onSubmitManual={handleMcpManualSubmit}
                    isLoading={isSavingMcpConnector || mcpFlow.state.kind === 'creating-draft'}
                    errorText={mcpConnectorError}
                />
            ) : null}
            <Modal
                isOpen={!!feishuCustomAppTool}
                onClose={() => {
                    if (!feishuCustomAppTool || !syncingTools.has(feishuCustomAppTool)) {
                        resetConnectionForm();
                    }
                }}
                title={t('website.integrationsSettings.connectFeishuTitle')}
                width={getIntegrationConnectGuideModalWidth(feishuCustomAppTool || 'Feishu', 'max-w-[560px]')}
                bodyClassName="px-6 pb-6 pt-1"
                titleClassName="text-[#1A162F]"
            >
                <div className="space-y-5">
                    <IntegrationConnectGuide toolName={feishuCustomAppTool || ''} />

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.feishuAppId')}</label>
                            <input
                                value={feishuCustomAppId}
                                onChange={(event) => setFeishuCustomAppId(event.target.value)}
                                placeholder="cli_a..."
                                className="w-full rounded-[10px] border border-[#E8EBF3] px-3 py-2 text-sm text-text-main outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F]"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.feishuAppSecret')}</label>
                            <input
                                type="password"
                                value={feishuCustomAppSecret}
                                onChange={(event) => setFeishuCustomAppSecret(event.target.value)}
                                placeholder={t('website.integrationsSettings.feishuAppSecretPlaceholder')}
                                className="w-full rounded-[10px] border border-[#E8EBF3] px-3 py-2 text-sm text-text-main outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F]"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={resetConnectionForm}
                            disabled={feishuCustomAppTool ? syncingTools.has(feishuCustomAppTool) : false}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleFeishuCustomAppConnection}
                            disabled={
                                !feishuCustomAppId.trim()
                                || !feishuCustomAppSecret.trim()
                                || !getFeishuOAuthRedirectUri()
                                || (feishuCustomAppTool ? syncingTools.has(feishuCustomAppTool) : false)
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {feishuCustomAppTool && syncingTools.has(feishuCustomAppTool) ? (
                                <CircleNotch size={15} weight="regular" className="animate-spin" />
                            ) : null}
                            {t('website.integrationsSettings.connectFeishuAction')}
                        </button>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={!!viewingDetailsConnection}
                onClose={() => setViewingDetailsConnection(null)}
                title={t('website.integrationsSettings.connectionDetailsTitle', { tool: viewingDetailsConnection?.integrationType || '' })}
                variant="reference"
                width="max-w-md"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="divide-y divide-gray-100">
                    {viewingDetailsConnection?.subAccounts?.map((sub) => (
                        <div key={`${sub.accountName}-${sub.customerId}`} className="py-4 first:pt-2 last:pb-2 flex items-start gap-3">
                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#3B82F6] shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                            <div className="space-y-1.5">
                                <div className="text-sm font-normal text-text-main flex items-baseline gap-1">
                                    <span className="text-gray-400 font-medium text-xs">{t('website.integrationsSettings.idLabel')}:</span>
                                    <span className="text-[#1a162f]">{sub.customerId}</span>
                                </div>
                                <div className="text-sm font-normal text-text-main flex items-baseline gap-1">
                                    <span className="text-gray-400 font-medium text-xs">{t('website.integrationsSettings.nameLabel')}:</span>
                                    <span className="text-[#1a162f]">{sub.accountName}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default IntegrationsSettings;
